import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** Gran Vía's axis (m): through this point at our feet, running away up the avenue to the right. */
// (owner: further off) moved left with the distance so the Metropolis keeps to the left of the words
const GV = { x: -72, z: 0 };
const GV_DIR: [ number, number ] = ( () => { const x = 0.24, z = 0.97, n = Math.hypot( x, z ); return [ x / n, z / n ]; } )();
/** Gran Vía's half width between its two building lines (m). */
const GV_HALF = 16;
/** Calle de Alcalá's near and far kerbs across our view (m from us). */
// (owner: the buildings further off) the street and everything beyond it pushed back
const ALCALA = { near: 84, far: 130 };
/** The Metropolis building's rotunda: its radius (m); its centre is found at the corner of the two streets. */
const ROT_R = 7.5;
/** Alcalá's median between its two pairs of lanes (m from us). */
const MEDIAN_Z = 107;

/** A car on the road, in metres: which lane, where along it, how fast, what kind. */
interface Car {
  /** 0 along Alcalá (x), 1 up and down Gran Vía (along its axis). */
  road: 0 | 1;
  lane: number;
  s: number;
  dir: 1 | -1;
  speed: number;
  /** 0 a Madrid taxi (white, the red band), otherwise an ordinary car's colour index. */
  kind: number;
  rank: number;
  shown: number;
}

/** A white stork in the sky, in page pixels. */
interface Stork {
  base: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  bob: number;
  phase: number;
  flap: number;
  rank: number;
  shown: number;
}

/**
 * Madrid's hero board (2026-10-18), after the owner's photograph: the corner of Gran Vía and Calle de Alcalá
 * on a bright summer afternoon, in true perspective (a camera 9 m up, everything in metres). A blue sky with
 * big white clouds drifting; the Metropolis building on the corner, its white rotunda with paired columns,
 * sculpture and the METROPOLIS band under the black slate dome dressed in gold and the winged Victory on
 * top; its wings along both streets under dark mansards; the Grassy building's small round tower up Gran
 * Vía and the avenue's white fronts going away to the right; plane trees along Alcalá. Madrid's white
 * taxis with their red band and the city's cars run along both streets, more of them on the charge. The
 * page's wallpaper is the storks. White storks wheel over the city, more of them on the charge (owner). Sized
 * by the stage's height.
 */
@Component( {
  selector: 'app-hero-madrid',
  template: '<canvas #canvas class="hmad-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hmad-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroMadridComponent extends HeroMotifBase {

  /** Offset of the blocks' own seeds. */
  private seedOfs = 911;
  /** The sky's clouds, drifting: one tile drawn twice. */
  private clouds: HTMLCanvasElement | null = null;
  /** The sky. */
  private sky: HTMLCanvasElement | null = null;
  /** The streets, the buildings and the trees beyond the cars. */
  private back: HTMLCanvasElement | null = null;
  /** Alcalá's flowered median. */
  private median: HTMLCanvasElement | null = null;
  /** The near poles and lamps, over the cars. */
  private front: HTMLCanvasElement | null = null;
  private cars: Car[] = [];
  private storks: Stork[] = [];
  private scaleK = 1;
  private eye = 9;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
  }

  /** Project a point: x across (metres from the axis), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  /** A point on Gran Vía: `s` metres up its axis from our feet, `v` metres to its right. */
  private gv( s: number, v: number ): [ number, number ] {
    return [ GV.x + GV_DIR[ 0 ] * s + GV_DIR[ 1 ] * v, GV.z + GV_DIR[ 1 ] * s - GV_DIR[ 0 ] * v ];
  }

  /** The rotunda's centre: in from the corner of Gran Vía's left building line and Alcalá's far kerb. */
  private rotunda(): [ number, number ] {
    const s = ALCALA.far / GV_DIR[ 1 ];
    const [ cx, cz ] = this.gv( s, -GV_HALF );
    return [ cx - GV_DIR[ 1 ] * ROT_R + GV_DIR[ 0 ] * ROT_R, cz + GV_DIR[ 0 ] * ROT_R + GV_DIR[ 1 ] * ROT_R ];
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 1.3 * H;
    this.hz = 0.68 * H;
    this.vx = W / 2;
    this.back = null;
    const r = this.seededLocal( 29 );
    this.buildStorks();
    // the traffic: a few cars at rest, the streets full on the charge (owner's photograph: Madrid's taxis)
    this.cars = [];
    const alcalaLanes = [ 90, 97, 116, 123 ];
    for ( let i = 0; i < 26; i++ ) {
      // (owner) only on the road across the front, none up the side streets
      const road = 0 as 0 | 1;
      const lane = road === 0 ? alcalaLanes[ i % 4 ] : [ -10, -4, 4, 10 ][ i % 4 ];
      const dir = ( road === 0 ? ( lane < 107 ? 1 : -1 ) : ( lane < 0 ? 1 : -1 ) ) as 1 | -1;
      this.cars.push( {
        road, lane, s: road === 0 ? -400 + r() * 800 : 110 + r() * 500, dir, speed: 8 + r() * 6,
        kind: r() < 0.45 ? 0 : 1 + Math.floor( r() * 6 ), rank: i < 4 ? 0 : 0.1 + r() * 0.9, shown: 0,
      } );
    }
  }

  /** (owner) the white storks wheeling over the city, a pair at rest, many on the charge. */
  private buildStorks(): void {
    const H = this.height, r = this.seededLocal( 89 ), k = this.scaleK;
    this.storks = [];
    for ( let i = 0; i < 18; i++ ) {
      const far = r();
      this.storks.push( {
        base: H * ( 0.06 + 0.3 * r() ), dir: r() < 0.5 ? 1 : -1, speed: ( 55 + r() * 35 ) * k * ( 1 - 0.4 * far ),
        // (owner: a little bigger)
        size: ( 30 - 11 * far ) * k, bob: ( 4 + r() * 8 ) * k, phase: r() * Math.PI * 2, flap: r() * 10,
        rank: i < 2 ? 0 : 0.08 + r() * 0.92, shown: 0,
      } );
    }
  }

  private drawStorks( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, H = this.height, e = this.energy;
    const share = 0.06 + 0.94 * e;
    for ( const b of this.storks ) {
      const want = b.rank <= share ? 1 : 0;
      b.shown = dt > 0 ? b.shown + ( want - b.shown ) * Math.min( 1, dt * 0.6 ) : want;
      // each its own wide loop about its own point, wheeling back the way it came; slow beats, long glides
      const cx = W * ( 0.1 + 0.8 * ( ( b.base * 0.0137 + b.size * 0.31 ) % 1 ) ), cy = H * 0.2;
      const rx = W * ( 0.18 + 0.22 * ( b.speed - 25 ) / 40 ), ry = H * ( 0.04 + 0.05 * ( b.bob / ( 12 * this.scaleK ) ) );
      if ( dt > 0 ) {
        // (owner: never staying in place; a little faster on the charge) each flies on across the whole sky
        // and comes in again from the other side
        b.phase += dt * b.dir * b.speed * ( 1 + 0.8 * e );
        b.flap += dt * ( 1 + 0.5 * e );
      }
      void rx;
      if ( b.shown < 0.02 ) { continue; }
      const th = b.phase + b.base;
      // (owner: higher, at least 40px above the Victory on the Metropolis) each in its own band of the sky
      // between the stage's top and that line
      const [ amx, amz ] = this.rotunda(), angel = this.P( amx, 51.5, amz )[ 1 ];
      const top = H * 0.04, bottom = angel - 40 * H / 666 - b.size * 0.5;
      const lane = ( ( b.base / H - 0.06 ) / 0.3 ), band = Math.max( 4, bottom - top );
      const span = W + b.size * 6, x = ( ( ( cx + b.phase ) % span ) + span ) % span - b.size * 3;
      const y = top + band * ( 0.12 + 0.76 * lane ) + Math.sin( th * 0.004 + b.base ) * band * 0.08;
      void cy; void ry; void th;
      const heading = b.dir, depth = 0.85 + 0.15 * Math.sin( b.base );
      const glide = Math.min( 1, Math.max( 0, ( Math.sin( b.flap * 0.45 ) + 0.1 ) / 0.5 ) );
      const theta = b.flap * Math.PI * 2 * 0.9;
      const arm = Math.sin( theta ) * ( 1 - glide ) - 0.05 * glide, hand = Math.sin( theta - 0.7 ) * ( 1 - glide ) + 0.05 * glide;
      ctx.save();
      ctx.globalAlpha = Math.pow( b.shown, 0.7 );
      ctx.translate( x, y - ( 1 - b.shown ) * b.bob * 2 );
      ctx.scale( heading * depth, depth );
      this.stork( ctx, b.size, arm, hand );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * One white stork flying, facing right (y down): its neck stretched out ahead, the long red bill, the red
   * legs trailing behind; broad wings white at the shoulder with black flight feathers, fingered at the tip.
   */
  private stork( ctx: CanvasRenderingContext2D, s: number, arm: number, hand: number ): void {
    const white: RGB = [ 250, 250, 246 ], black: RGB = [ 26, 26, 30 ], red: RGB = [ 214, 70, 46 ];
    const fillP = ( pts: [ number, number ][], c: RGB ) => {
      ctx.fillStyle = this.rgba( c, 1 );
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
      ctx.closePath();
      ctx.fill();
    };
    // (owner: the black on its wings was wrong) seen from the side the wings rise and fall across the body,
    // not along it: each one from the shoulder out to its fingered tip, its leading half white, the trailing
    // half and the long primaries black; the far wing behind the body, the near one before it
    const wing = ( lift: number, bend: number, dx: number, dy: number, shade: number ) => {
      const L = 1.05, el = Math.max( -1, Math.min( 1, lift ) ), fore = 0.3 + 0.7 * Math.abs( el );
      const rootF: [ number, number ] = [ 0.16 + dx, -0.02 + dy ], rootB: [ number, number ] = [ -0.16 + dx, 0.0 + dy ];
      const elbow: [ number, number ] = [ -0.06 + dx, dy - el * L * 0.48 * fore - 0.02 ];
      const handEl = Math.max( -1, Math.min( 1, el + bend * 0.35 ) );
      const tip: [ number, number ] = [ -0.2 + dx, dy - handEl * L * fore - 0.04 ];
      const lerp = ( a: [ number, number ], b: [ number, number ], t: number ): [ number, number ] => [ a[ 0 ] + ( b[ 0 ] - a[ 0 ] ) * t, a[ 1 ] + ( b[ 1 ] - a[ 1 ] ) * t ];
      const chord = ( p2: [ number, number ], w: number ): [ number, number ][] => [ [ p2[ 0 ] + w * 0.55, p2[ 1 ] ], [ p2[ 0 ] - w * 0.45, p2[ 1 ] ] ];
      // a broad wing: wide at the elbow, still broad out to the fingered hand
      const [ eF, eB ] = chord( elbow, 0.48 ), [ tF ] = chord( tip, 0.22 );
      const wc = ( c: RGB ) => this.mix( c, [ 120, 130, 150 ], shade );
      // the whole wing black, then the white coverts over its leading half near the body
      const fingers: [ number, number ][] = [];
      for ( let q = 0; q < 5; q++ ) {
        const t = q / 4, base = lerp( tip, eB, 0.15 + t * 0.35 );
        fingers.push( [ base[ 0 ] - 0.05 - t * 0.02, base[ 1 ] - Math.sign( handEl || 1 ) * 0.06 ], [ base[ 0 ] - 0.02, base[ 1 ] ] );
      }
      fillP( [ rootF, eF, tF, tip, ...fingers, eB, rootB ], wc( black ) );
      // the white: the leading half of the wing from the body out past the elbow toward the hand
      fillP( [ rootF, eF, lerp( eF, tF, 0.55 ), lerp( lerp( tF, tip, 0.5 ), lerp( eF, eB, 0.5 ), 0.45 ), lerp( eF, eB, 0.52 ), lerp( rootF, rootB, 0.55 ) ], wc( white ) );
    };
    const theta = arm, bendK = hand - arm;
    // the far wing, a little behind and above, in the body's shade
    wing( theta, bendK, -0.05, -0.04, 0.22 );
    // (owner: fuller, the legs not a single line) two red legs trailing, each tapering to its foot
    for ( const off of [ -0.025, 0.03 ] ) {
      fillP( [ [ -0.22, 0.04 + off ], [ -0.22, 0.09 + off ], [ -0.9, 0.12 + off * 1.6 ], [ -0.9, 0.105 + off * 1.6 ] ], this.mix( red, [ 120, 30, 20 ], off > 0 ? 0 : 0.25 ) );
      fillP( [ [ -0.9, 0.1 + off * 1.6 ], [ -1.0, 0.09 + off * 1.6 ], [ -1.0, 0.13 + off * 1.6 ], [ -0.9, 0.125 + off * 1.6 ] ], this.mix( red, [ 120, 30, 20 ], 0.2 ) );
    }
    // the body, full and rounded, lit above, a cool shade beneath
    const bg = ctx.createLinearGradient( 0, -0.14 * s, 0, 0.18 * s );
    bg.addColorStop( 0, this.rgba( white, 1 ) );
    bg.addColorStop( 1, this.rgba( [ 214, 216, 222 ], 1 ) );
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.moveTo( 0.3 * s, -0.02 * s );
    ctx.bezierCurveTo( 0.22 * s, -0.16 * s, -0.22 * s, -0.16 * s, -0.36 * s, -0.02 * s );
    ctx.lineTo( -0.5 * s, 0.0 );
    ctx.lineTo( -0.48 * s, 0.07 * s );
    ctx.bezierCurveTo( -0.2 * s, 0.17 * s, 0.2 * s, 0.15 * s, 0.3 * s, 0.04 * s );
    ctx.closePath();
    ctx.fill();
    // the neck, thick at the shoulder and slimming to the head
    fillP( [ [ 0.24, -0.07 ], [ 0.6, -0.08 ], [ 0.62, -0.01 ], [ 0.24, 0.05 ] ], white );
    ctx.fillStyle = this.rgba( white, 1 );
    ctx.beginPath(); ctx.ellipse( 0.64 * s, -0.045 * s, s * 0.075, s * 0.058, 0, 0, Math.PI * 2 ); ctx.fill();
    // the eye, the long red bill tapering to its point
    ctx.fillStyle = this.rgba( black, 1 );
    ctx.beginPath(); ctx.arc( 0.665 * s, -0.06 * s, s * 0.012, 0, Math.PI * 2 ); ctx.fill();
    fillP( [ [ 0.69, -0.07 ], [ 0.98, -0.03 ], [ 0.69, -0.02 ] ], red );
    // the near wing over the body
    wing( theta * 0.95, bendK, 0.02, 0.03, 0 );
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // (owner) the page's wallpaper is the storks
      this.drawWallStorks( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front || !this.sky || !this.clouds ) { return; }
    ctx.drawImage( this.sky, 0, 0, W, H );
    // the clouds drift slowly to the right, whole pixels so the tile's seam never shows
    const off = Math.round( ( performance.now() / 1000 * 3 * this.scaleK ) % W );
    ctx.drawImage( this.clouds, off - W, 0, W, H );
    ctx.drawImage( this.clouds, off, 0, W, H );
    ctx.drawImage( this.back, 0, 0, W, H );
    this.moveCars( dt );
    const order = this.cars.filter( c => c.shown > 0.02 ).map( c => ( { c, z: this.carAt( c, 0 )[ 1 ] } ) ).sort( ( a, b ) => b.z - a.z );
    for ( const { c, z } of order ) { if ( z > MEDIAN_Z ) { this.drawCar( ctx, c ); } }
    if ( this.median ) { ctx.drawImage( this.median, 0, 0, W, H ); }
    for ( const { c, z } of order ) { if ( z <= MEDIAN_Z ) { this.drawCar( ctx, c ); } }
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawStorks( ctx, dt );
  }

  /** A car's ground point (x, z) `u` metres ahead of its middle. */
  private carAt( c: Car, u: number ): [ number, number ] {
    if ( c.road === 0 ) { return [ c.s + c.dir * u, c.lane ]; }
    return this.gv( c.s + c.dir * u, c.lane );
  }

  private moveCars( dt: number ): void {
    // (owner) the charge never adds cars: the same share is on the road at rest and on the charge
    const share = 0.18;
    for ( const c of this.cars ) {
      const want = c.rank <= share ? 1 : 0;
      c.shown = dt > 0 ? c.shown + ( want - c.shown ) * Math.min( 1, dt * 1.5 ) : want;
      if ( dt <= 0 ) { continue; }
      c.s += c.dir * c.speed * dt * ( 1 + 0.3 * this.energy );
      if ( c.road === 0 ) {
        const half = ( this.width / 2 + 80 ) * c.lane / this.F;
        if ( c.s > half ) { c.s = -half; } else if ( c.s < -half ) { c.s = half; }
      } else if ( c.s > 700 ) { c.s = ALCALA.far / GV_DIR[ 1 ] + 4; } else if ( c.s < ALCALA.far / GV_DIR[ 1 ] + 2 ) { c.s = 700; }
    }
  }

  /**
   * One car: its shadow on the asphalt, the body, the glasshouse, the faces the eye can see. Madrid's taxis
   * white with the red band along the doors; the others in the city's usual colours.
   */
  private drawCar( ctx: CanvasRenderingContext2D, c: Car ): void {
    const cols: RGB[] = [ [ 246, 246, 244 ], [ 28, 30, 34 ], [ 150, 152, 158 ], [ 184, 40, 36 ], [ 34, 54, 96 ], [ 210, 210, 206 ], [ 110, 96, 80 ] ];
    const col = c.kind === 0 ? cols[ 0 ] : cols[ c.kind ];
    const [ px, pz ] = this.carAt( c, 0 ), [ qx, qz ] = this.carAt( c, 1 );
    const ux = qx - px, uz = qz - pz, vx = -uz, vz = ux;
    // (owner: on the widest screens the cars stretched, as in Rio) a car is drawn as seen from no wider an
    // angle than about 39 degrees and slid to its place, so at the frame's edge it keeps its real shape
    const lim = 0.8 * pz, pc = Math.max( -lim, Math.min( lim, px ) ), slide = this.F * ( px - pc ) / pz;
    const at = ( u: number, v: number, y: number ): [ number, number ] => {
      const [ sx, sy ] = this.P( pc + ux * u + vx * v, y, pz + uz * u + vz * v );
      return [ sx + slide, sy ];
    };
    const sees = ( nu: number, nv: number, u: number, v: number ) => {
      const nx = ux * nu + vx * nv, nz = uz * nu + vz * nv;
      const fx = pc + ux * u + vx * v, fz = pz + uz * u + vz * v;
      return nx * -fx + nz * -fz > 0;
    };
    const fill = ( pts: [ number, number ][], f: string ) => { ctx.fillStyle = f; ctx.beginPath(); pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x, y ) : ctx.moveTo( x, y ) ); ctx.closePath(); ctx.fill(); };
    const shade = ( k: number ) => this.rgba( this.mix( col, k > 0 ? [ 255, 250, 236 ] : [ 40, 46, 60 ], Math.abs( k ) ), 1 );
    const box = ( u0: number, u1: number, v0: number, v1: number, y0: number, y1: number, top: string, lit: string, dark: string ) => {
      const sides: [ number, number, number, number, number, number ][] = [ [ u0, v0, u1, v0, 0, -1 ], [ u1, v0, u1, v1, 1, 0 ], [ u1, v1, u0, v1, 0, 1 ], [ u0, v1, u0, v0, -1, 0 ] ];
      for ( const [ ua, va, ub, vb, nu, nv ] of sides ) {
        if ( !sees( nu, nv, ( ua + ub ) / 2, ( va + vb ) / 2 ) ) { continue; }
        fill( [ at( ua, va, y0 ), at( ub, vb, y0 ), at( ub, vb, y1 ), at( ua, va, y1 ) ], nv !== 0 ? lit : dark );
      }
      fill( [ at( u0, v0, y1 ), at( u1, v0, y1 ), at( u1, v1, y1 ), at( u0, v1, y1 ) ], top );
    };
    // (owner: the cars finer and more detailed) a saloon in two tiers: the lower body with its dark bumpers,
    // the shoulder, the hood and boot, the cabin's glass split by its pillars, the doors' lines, mirrors,
    // wheels with their rims, the plate and the lamps; Madrid's taxis white with the red diagonal band on the
    // front door and the lit TAXI sign on the roof
    const glass = 'rgb(46,58,72)', glassLit = 'rgb(92,112,134)', trim = 'rgb(40,40,44)';
    ctx.save();
    ctx.globalAlpha = Math.min( 1, c.shown );
    fill( [ at( -2.4, -1.2, 0.02 ), at( 2.4, -1.2, 0.02 ), at( 2.6, 1.4, 0.02 ), at( -2.2, 1.4, 0.02 ) ], 'rgba(20,20,30,0.3)' );
    for ( const wu of [ -1.45, 1.45 ] ) {
      for ( const wv of [ -0.92, 0.92 ] ) {
        if ( !sees( 0, wv < 0 ? -1 : 1, wu, wv ) ) { continue; }
        const [ wx, wy ] = at( wu, wv, 0.33 ), k = this.F / ( pz + uz * wu + vz * wv );
        ctx.fillStyle = 'rgb(22,22,26)';
        ctx.beginPath(); ctx.ellipse( wx, wy, k * 0.34, k * 0.33, 0, 0, Math.PI * 2 ); ctx.fill();
        ctx.fillStyle = 'rgb(176,178,184)';
        ctx.beginPath(); ctx.ellipse( wx, wy, k * 0.18, k * 0.17, 0, 0, Math.PI * 2 ); ctx.fill();
      }
    }
    // the lower body and the bumper band round it
    box( -2.25, 2.25, -0.9, 0.9, 0.25, 0.86, shade( 0.25 ), shade( 0.05 ), shade( -0.25 ) );
    box( -2.27, 2.27, -0.91, 0.91, 0.25, 0.42, trim, trim, trim );
    // the shoulder: the hood forward, the boot behind, the beltline under the glass
    box( -2.15, 2.15, -0.86, 0.86, 0.86, 1.0, shade( 0.35 ), shade( 0.12 ), shade( -0.18 ) );
    for ( const v of [ -0.87, 0.87 ] ) {
      if ( !sees( 0, v < 0 ? -1 : 1, 0, v ) ) { continue; }
      // the side glass in two panes, the pillars between, the door lines, a mirror
      fill( [ at( -1.25, v * 0.92, 1.0 ), at( -0.12, v * 0.92, 1.0 ), at( -0.12, v * 0.88, 1.5 ), at( -0.95, v * 0.88, 1.5 ) ], glassLit );
      fill( [ at( 0.02, v * 0.92, 1.0 ), at( 1.15, v * 0.92, 1.0 ), at( 0.7, v * 0.88, 1.5 ), at( 0.02, v * 0.88, 1.5 ) ], glassLit );
      fill( [ at( -0.12, v * 0.92, 1.0 ), at( 0.02, v * 0.92, 1.0 ), at( 0.02, v * 0.88, 1.5 ), at( -0.12, v * 0.88, 1.5 ) ], trim );
      for ( const du of [ -1.05, -0.05, 1.0 ] ) { fill( [ at( du, v, 0.45 ), at( du + 0.03, v, 0.45 ), at( du + 0.03, v, 0.98 ), at( du, v, 0.98 ) ], 'rgba(30,30,36,0.55)' ); }
      fill( [ at( 1.0, v * 1.12, 0.98 ), at( 1.18, v * 1.12, 0.98 ), at( 1.18, v * 1.12, 1.12 ), at( 1.0, v * 1.12, 1.12 ) ], trim );
      if ( c.kind === 0 ) {
        // the taxi's red band across the front door, rising toward the front
        fill( [ at( 0.05, v * 1.0, 0.5 ), at( 0.3, v * 1.0, 0.5 ), at( 0.95, v * 1.0, 0.95 ), at( 0.7, v * 1.0, 0.95 ) ], 'rgb(196,36,34)' );
      }
    }
    // the cabin's roof, the windscreen and the rear window leaning back
    fill( [ at( -0.95, -0.76, 1.52 ), at( 0.7, -0.76, 1.52 ), at( 0.7, 0.76, 1.52 ), at( -0.95, 0.76, 1.52 ) ], shade( 0.3 ) );
    if ( sees( 1, 0, 1.2, 0 ) ) { fill( [ at( 1.2, -0.82, 1.0 ), at( 1.2, 0.82, 1.0 ), at( 0.7, 0.76, 1.52 ), at( 0.7, -0.76, 1.52 ) ], glass ); }
    if ( sees( -1, 0, -1.3, 0 ) ) { fill( [ at( -1.3, -0.82, 1.0 ), at( -1.3, 0.82, 1.0 ), at( -0.95, 0.76, 1.52 ), at( -0.95, -0.76, 1.52 ) ], glass ); }
    // the lamps and the plate on the ends we see
    for ( const [ u, c2 ] of [ [ 2.26, 'rgb(250,246,220)' ], [ -2.26, 'rgb(200,40,36)' ] ] as [ number, string ][] ) {
      if ( !sees( u > 0 ? 1 : -1, 0, u, 0 ) ) { continue; }
      const k = this.F / pz;
      for ( const v of [ -0.62, 0.62 ] ) { const [ lx, ly ] = at( u, v, 0.72 ); ctx.fillStyle = c2; ctx.fillRect( lx - k * 0.13, ly - k * 0.07, k * 0.26, k * 0.14 ); }
      const [ plx, ply ] = at( u + Math.sign( u ) * 0.02, 0, 0.36 );
      ctx.fillStyle = 'rgb(240,240,236)';
      ctx.fillRect( plx - k * 0.26, ply - k * 0.06, k * 0.52, k * 0.12 );
    }
    if ( c.kind === 0 ) {
      // the roof sign: a small white box, its green light lit for hire
      box( -0.25, 0.25, -0.32, 0.32, 1.52, 1.72, 'rgb(250,250,248)', 'rgb(240,240,236)', 'rgb(206,206,206)' );
      const [ gx, gy ] = at( 0.1, 0, 1.78 ), k = this.F / pz;
      ctx.fillStyle = 'rgb(70,210,90)';
      ctx.beginPath(); ctx.arc( gx, gy, k * 0.09, 0, Math.PI * 2 ); ctx.fill();
    }
    ctx.restore();
  }

  /** The wallpaper: the white storks, still in flight, scattered down the page. */
  private drawWallStorks( ctx: CanvasRenderingContext2D ): void {
    const W = this.width, H = this.height, r = this.seededLocal( 9 );
    const n = Math.max( 5, Math.round( W * H / 90000 ) );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, sz = ( 34 + r() * 22 ) * this.scaleK, a = r() * 2 - 1;
      ctx.save();
      ctx.globalAlpha = 0.45 + r() * 0.3;
      ctx.translate( x, y );
      ctx.scale( r() < 0.5 ? -1 : 1, 1 );
      this.stork( ctx, sz, a, a + ( r() - 0.5 ) * 0.6 );
      ctx.restore();
    }
  }

  /**
   * The still parts, painted once, far to near, in metres projected: the sky and its clouds; the streets;
   * the buildings sorted far to near (each an oriented block whose visible faces are lit by the afternoon
   * sun from the left), the Metropolis building with its rotunda and dome, the trees; then the near poles.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const hz = this.hz;
    const F = this.F;
    const [ cream, gold, amber, red ] = this.five();
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    let g: CanvasRenderingContext2D;
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    const skyBlue: RGB = [ 214, 196, 150 ], skyLow: RGB = this.mix( [ 250, 238, 206 ], cream, 0.3 );
    // the haze the warm pale gold of this sky
    const air = this.mix( skyLow, [ 246, 238, 220 ], 0.3 );
    // (kaizen 16) the summer haze up the avenue: far fronts paler and bluer
    // (hourly 10) a touch more summer haze far up the avenue
    const inAir = ( col: RGB, z: number, k = 0.88 ) => this.mix( col, air, ( 1 - Math.exp( -z / 560 ) ) * k );
    // (owner: the sun on the right) the afternoon sun from the upper right, a little toward us
    const SUN: [ number, number, number ] = ( () => { const v = [ 0.62, 0.55, -0.56 ], n = Math.hypot( ...v ); return [ v[ 0 ] / n, v[ 1 ] / n, v[ 2 ] / n ] as [ number, number, number ]; } )();
    const sunK = ( nx: number, nz: number ) => Math.max( 0, nx * SUN[ 0 ] + nz * SUN[ 2 ] ) / Math.hypot( SUN[ 0 ], SUN[ 2 ] );
    // (owner: the grey roofs in the sun, a little lighter)
    const stone: RGB = [ 242, 236, 224 ], stoneShade: RGB = [ 186, 188, 198 ], slate: RGB = [ 104, 108, 120 ];
    const faceCol = ( base: RGB, nx: number, nz: number, z: number ) => inAir( this.mix( this.mix( base, stoneShade, 0.55 ), this.mix( base, [ 255, 252, 244 ], 0.4 ), sunK( nx, nz ) ), z );
    const rr = ( seed: number ) => this.seededLocal( seed );
    // (owner: the flowers in detail) one flower: five petals round a golden heart, the petals lit at the right
    // by the sun, a dot when it is too small to show them
    const flower = ( x: number, y: number, rad: number, col: RGB, rot: number ) => {
      if ( rad < 0.9 ) {
        g.fillStyle = this.rgba( col, 1 );
        g.beginPath(); g.arc( x, y, Math.max( 0.5, rad ), 0, Math.PI * 2 ); g.fill();
        return;
      }
      for ( let q = 0; q < 5; q++ ) {
        const a = rot + q / 5 * Math.PI * 2, px = x + Math.cos( a ) * rad * 0.52, py = y + Math.sin( a ) * rad * 0.42;
        g.fillStyle = this.rgba( this.mix( col, Math.cos( a ) > 0.2 ? [ 255, 255, 250 ] : [ 90, 40, 50 ], Math.cos( a ) > 0.2 ? 0.25 : 0.12 ), 1 );
        g.beginPath(); g.ellipse( px, py, rad * 0.5, rad * 0.34, a, 0, Math.PI * 2 ); g.fill();
      }
      g.fillStyle = 'rgb(236,190,70)';
      g.beginPath(); g.arc( x, y, rad * 0.24, 0, Math.PI * 2 ); g.fill();
    };
    // one leaf: a pointed blade, its sunlit half lighter, a fine midrib
    const leafBlade = ( x: number, y: number, len: number, ang: number, col: RGB ) => {
      if ( len < 1.2 ) { g.fillStyle = this.rgba( col, 1 ); g.beginPath(); g.arc( x, y, Math.max( 0.5, len * 0.4 ), 0, Math.PI * 2 ); g.fill(); return; }
      const c = Math.cos( ang ), sn = Math.sin( ang ), w = len * 0.32;
      const tip: [ number, number ] = [ x + c * len, y + sn * len ];
      g.fillStyle = this.rgba( this.mix( col, [ 20, 40, 20 ], 0.25 ), 1 );
      g.beginPath(); g.moveTo( x, y ); g.quadraticCurveTo( x + c * len * 0.5 + sn * w, y + sn * len * 0.5 - c * w, tip[ 0 ], tip[ 1 ] ); g.quadraticCurveTo( x + c * len * 0.5 - sn * w, y + sn * len * 0.5 + c * w, x, y ); g.fill();
      g.fillStyle = this.rgba( this.mix( col, [ 200, 230, 140 ], 0.3 ), 1 );
      g.beginPath(); g.moveTo( x, y ); g.quadraticCurveTo( x + c * len * 0.5 + sn * w, y + sn * len * 0.5 - c * w, tip[ 0 ], tip[ 1 ] ); g.lineTo( x, y ); g.fill();
    };

    // ---- the sky and its clouds ----
    const sk = canvas();
    g = sk.g;
    const sg = g.createLinearGradient( 0, 0, 0, hz );
    // (kaizen 2) the photograph's summer sky: deep blue overhead, paling through clear blue to the haze
    // (owner: the sky lighter, a pale yellow sun on it, so the words read) a light summer blue warming to
    // pale gold near the horizon
    // (owner: yellower, the sun full on it) a light blue overhead turning to sunlit pale gold downward
    // (owner: not blue, a pale yellow sky, the sunlight spread wide) warm pale gold from top to horizon
    // (owner: pale yellow and white) a pale yellow overhead, nearly white in the middle, warm again low
    // (owner: deeper yellow at the top, whiter at the bottom)
    sg.addColorStop( 0, this.rgba( [ 240, 212, 136 ], 1 ) );
    sg.addColorStop( 0.5, this.rgba( [ 250, 234, 186 ], 1 ) );
    sg.addColorStop( 1, this.rgba( [ 255, 252, 244 ], 1 ) );
    g.fillStyle = sg;
    g.fillRect( 0, 0, W, hz + 2 );
    // (kaizen 5) the sky warmer toward the sun
    {
      // (owner: a sun on the right) the sun high at the right: a soft broad warmth, a pale yellow glow and
      // its bright heart, no rim
      const sx = W - H * 0.42, sy = H * 0.13;
      const wg = g.createRadialGradient( sx, sy, 0, sx, sy, Math.max( W, H ) * 1.1 );
      wg.addColorStop( 0, 'rgba(255,250,220,0.9)' );
      wg.addColorStop( 0.25, 'rgba(255,246,206,0.55)' );
      wg.addColorStop( 0.6, 'rgba(255,244,210,0.2)' );
      wg.addColorStop( 1, 'rgba(255,244,210,0)' );
      g.fillStyle = wg;
      g.fillRect( 0, 0, W, hz );
      const sg2 = g.createRadialGradient( sx, sy, 0, sx, sy, H * 0.09 );
      sg2.addColorStop( 0, 'rgba(255,255,248,1)' );
      sg2.addColorStop( 0.45, 'rgba(255,252,226,0.95)' );
      sg2.addColorStop( 1, 'rgba(255,240,180,0)' );
      g.fillStyle = sg2;
      g.fillRect( sx - H * 0.1, sy - H * 0.1, H * 0.2, H * 0.2 );
    }
    this.sky = sk.c;
    const cl = canvas();
    g = cl.g;
    {
      // (owner: the clouds were poor; as the other themes) Istanbul's approved clouds: level banks of white
      // heaped above, their puffs drawn from the bottom up so the sunlit tops lie over the grey blue bases,
      // set out by the stage's height and repeated a stage-width either side so the drift wraps unseen
      const cr = rr( 29 ), white: RGB = [ 255, 255, 255 ];
      const banks: [ number, number, number ][] = [];
      for ( let bx = cr() * 0.3 * H; bx < W + H * 0.3; bx += ( 0.6 + cr() * 0.45 ) * H ) { banks.push( [ bx, 0.1 + cr() * 0.2, ( 0.3 + cr() * 0.22 ) * H ] ); }
      for ( const [ bx, v, bw ] of banks ) {
        const by = H * v;
        const puffs: [ number, number, number ][] = [];
        for ( let i = 0; i < 30; i++ ) {
          const a = cr() * 2 - 1, heap = Math.sqrt( 1 - a * a );
          puffs.push( [ bx + a * bw * 0.5, by - cr() * heap * H * 0.07, ( 0.4 + cr() * 0.5 ) * H * 0.045 * ( 0.6 + heap ) ] );
        }
        puffs.sort( ( p2, q2 ) => q2[ 1 ] - p2[ 1 ] );
        for ( const [ px, py, pr ] of puffs ) {
          const up = Math.max( 0, Math.min( 1, ( by - py ) / ( H * 0.07 ) ) );
          const tone = this.mix( this.mix( [ 200, 214, 226 ], skyBlue, 0.12 ), white, 0.45 + 0.55 * up );
          // each copy its own gradient at its own place, so the tile repeats without a seam
          for ( const dx2 of [ -W, 0, W ] ) {
            const pg = g.createRadialGradient( px + dx2, py - pr * 0.25, 0, px + dx2, py, pr );
            pg.addColorStop( 0, this.rgba( this.mix( tone, white, 0.4 ), 0.95 ) );
            pg.addColorStop( 0.7, this.rgba( tone, 0.7 ) );
            pg.addColorStop( 1, this.rgba( tone, 0 ) );
            g.fillStyle = pg;
            g.fillRect( px + dx2 - pr, py - pr, pr * 2, pr * 2 );
          }
        }
      }
    }
    this.clouds = cl.c;

    let drawMedian: ( () => void ) | null = null;
    // ---- the back layer: streets, buildings, trees ----
    const bk = canvas();
    g = bk.g;
    // the ground: the plaza's asphalt, Alcalá's lanes, Gran Vía going away, the pavements
    {
      const gg = g.createLinearGradient( 0, hz, 0, H );
      gg.addColorStop( 0, this.rgba( inAir( [ 150, 146, 142 ], 600 ), 1 ) );
      gg.addColorStop( 1, this.rgba( [ 112, 110, 112 ], 1 ) );
      g.fillStyle = gg;
      g.fillRect( 0, hz - 2, W, H - hz + 2 );
      const half = ( z: number ) => ( W / 2 + 80 ) * z / F;
      // Alcalá's far pavement under the buildings, the near one before the plaza
      for ( const [ z0, z1 ] of [ [ ALCALA.far, ALCALA.far + 6 ], [ ALCALA.near - 6, ALCALA.near ] ] ) {
        poly( [ P( -half( z1 ), 0.15, z1 ), P( half( z1 ), 0.15, z1 ), P( half( z0 ), 0.15, z0 ), P( -half( z0 ), 0.15, z0 ) ], this.rgba( inAir( [ 214, 206, 192 ], z0 ), 1 ) );
      }
      // (kaizen) the plaza at our feet: pale stone paving with its joints running away to the kerb
      {
        const z0 = 20, z1 = ALCALA.near - 6;
        const pg = g.createLinearGradient( 0, P( 0, 0, z1 )[ 1 ], 0, H );
        pg.addColorStop( 0, this.rgba( inAir( [ 222, 214, 200 ], z1 ), 1 ) );
        pg.addColorStop( 1, this.rgba( [ 200, 192, 180 ], 1 ) );
        poly( [ P( -half( z1 ), 0.16, z1 ), P( half( z1 ), 0.16, z1 ), P( half( z0 ), 0.16, z0 ), P( -half( z0 ), 0.16, z0 ) ], pg );
        g.strokeStyle = 'rgba(150,140,128,0.35)';
        g.lineWidth = 0.6;
        for ( let z = z0 + 1; z < z1; z += 1.2 + ( z - z0 ) * 0.04 ) {
          const a2 = P( -half( z ), 0.17, z ), b2 = P( half( z ), 0.17, z );
          g.beginPath(); g.moveTo( a2[ 0 ], a2[ 1 ] ); g.lineTo( b2[ 0 ], b2[ 1 ] ); g.stroke();
        }
        for ( let x = -120; x <= 120; x += 1.6 ) {
          const a2 = P( x, 0.17, z0 ), b2 = P( x, 0.17, z1 );
          g.beginPath(); g.moveTo( a2[ 0 ], a2[ 1 ] ); g.lineTo( b2[ 0 ], b2[ 1 ] ); g.stroke();
        }
        // (hourly 5) the paving's stones a little uneven in tone
        {
          const r6 = rr( 79 );
          for ( let q = 0; q < 160; q++ ) {
            const x = ( r6() - 0.5 ) * 200, z = z0 + r6() * ( z1 - z0 ), w = 1.6, d = 1.2 + ( z - z0 ) * 0.04;
            poly( [ P( x, 0.17, z ), P( x + w, 0.17, z ), P( x + w, 0.17, z + d ), P( x, 0.17, z + d ) ], r6() < 0.5 ? 'rgba(255,250,240,0.18)' : 'rgba(130,120,110,0.12)' );
          }
        }
        // (kaizen 19) Madrid's paving: a band of darker granite every few metres across the plaza
        for ( let z = z0 + 3; z < z1 - 1; z += 6 ) {
          poly( [ P( -half( z + 0.8 ), 0.17, z + 0.8 ), P( half( z + 0.8 ), 0.17, z + 0.8 ), P( half( z ), 0.17, z ), P( -half( z ), 0.17, z ) ], 'rgba(120,114,110,0.22)' );
        }
        // the granite kerb along the road
        poly( [ P( -half( z1 ), 0.16, z1 ), P( half( z1 ), 0.16, z1 ), P( half( z1 ), 0.0, z1 + 0.05 ), P( -half( z1 ), 0.0, z1 + 0.05 ) ], this.rgba( [ 160, 154, 148 ], 1 ) );
      }
      // (kaizen 15) the asphalt's wear: darker tracks along the lanes, a few patched squares
      for ( const z of [ 88, 92, 99, 114, 118, 125 ] ) {
        poly( [ P( -half( z ), 0.01, z - 0.5 ), P( half( z ), 0.01, z - 0.5 ), P( half( z ), 0.01, z + 0.5 ), P( -half( z ), 0.01, z + 0.5 ) ], 'rgba(60,58,62,0.08)' );
      }
      {
        const r2 = rr( 61 );
        for ( let q = 0; q < 30; q++ ) {
          const x = ( r2() - 0.5 ) * 300, z = ALCALA.near + 2 + r2() * ( ALCALA.far - ALCALA.near - 4 ), w = 1 + r2() * 3, d = 0.8 + r2() * 2;
          poly( [ P( x, 0.01, z ), P( x + w, 0.01, z ), P( x + w, 0.01, z + d ), P( x, 0.01, z + d ) ], r2() < 0.5 ? 'rgba(80,78,82,0.12)' : 'rgba(170,166,160,0.1)' );
        }
      }
      // the zebra crossing over the mouth of Gran Vía
      {
        const s0 = ALCALA.far / GV_DIR[ 1 ] + 4;
        for ( let v = -GV_HALF + 5; v < GV_HALF - 5; v += 1.6 ) {
          poly( [ P( ...this.gvxy( s0, v ) ), P( ...this.gvxy( s0, v + 0.8 ) ), P( ...this.gvxy( s0 + 4, v + 0.8 ) ), P( ...this.gvxy( s0 + 4, v ) ) ], 'rgba(250,250,246,0.75)' );
        }
      }
      // (kaizen 8-10) the far kerb's granite edge, the buildings' soft contact shadow on their pavement, a few
      // manhole covers in the road
      poly( [ P( -half( ALCALA.far ), 0.15, ALCALA.far ), P( half( ALCALA.far ), 0.15, ALCALA.far ), P( half( ALCALA.far ), 0, ALCALA.far - 0.05 ), P( -half( ALCALA.far ), 0, ALCALA.far - 0.05 ) ], this.rgba( inAir( [ 170, 164, 156 ], ALCALA.far ), 1 ) );
      poly( [ P( -half( ALCALA.far + 6 ), 0.16, ALCALA.far + 6 ), P( half( ALCALA.far + 6 ), 0.16, ALCALA.far + 6 ), P( half( ALCALA.far + 5 ), 0.16, ALCALA.far + 5 ), P( -half( ALCALA.far + 5 ), 0.16, ALCALA.far + 5 ) ], 'rgba(60,64,84,0.18)' );
      {
        const r5 = rr( 73 );
        for ( let q = 0; q < 6; q++ ) {
          const x = ( r5() - 0.5 ) * 160, z = ALCALA.near + 4 + r5() * ( ALCALA.far - ALCALA.near - 8 );
          const [ cx2, cy2 ] = P( x, 0.01, z ), k5 = F / z;
          g.fillStyle = 'rgba(56,54,58,0.55)';
          g.beginPath(); g.ellipse( cx2, cy2, k5 * 0.4, k5 * 0.4 * ( this.eye / z ) * 3, 0, 0, Math.PI * 2 ); g.fill();
        }
      }
      // (owner: flowers) Alcalá's raised median: a stone kerb round a bed of red geraniums among green leaves,
      // on its own layer (owner: the far lanes' cars pass behind it, the near lanes' before it)
      drawMedian = () => {
        const zm = MEDIAN_Z, r3 = rr( 67 );
        poly( [ P( -half( zm + 1.3 ), 0.35, zm + 1.3 ), P( half( zm + 1.3 ), 0.35, zm + 1.3 ), P( half( zm - 1.3 ), 0.35, zm - 1.3 ), P( -half( zm - 1.3 ), 0.35, zm - 1.3 ) ], this.rgba( inAir( [ 70, 96, 52 ], zm ), 1 ) );
        poly( [ P( -half( zm - 1.3 ), 0.35, zm - 1.3 ), P( half( zm - 1.3 ), 0.35, zm - 1.3 ), P( half( zm - 1.3 ), 0, zm - 1.3 ), P( -half( zm - 1.3 ), 0, zm - 1.3 ) ], this.rgba( inAir( [ 196, 190, 180 ], zm ), 1 ) );
        const k3 = F / zm, flowers: RGB[] = [ red, [ 214, 60, 70 ], [ 236, 120, 140 ], [ 250, 246, 240 ] ];
        // (hourly 7) a low clipped hedge along the bed's far side, the flowers dense before it
        poly( [ P( -half( zm + 1 ), 0.35, zm + 1 ), P( half( zm + 1 ), 0.35, zm + 1 ), P( half( zm + 1 ), 1.0, zm + 1 ), P( -half( zm + 1 ), 1.0, zm + 1 ) ], this.rgba( inAir( [ 52, 92, 46 ], zm ), 1 ) );
        poly( [ P( -half( zm + 1 ), 1.0, zm + 1 ), P( half( zm + 1 ), 1.0, zm + 1 ), P( half( zm + 1.2 ), 1.0, zm + 1.2 ), P( -half( zm + 1.2 ), 1.0, zm + 1.2 ) ], this.rgba( inAir( [ 96, 140, 70 ], zm ), 1 ) );
        // geraniums: a bed of leaves, then round heads of five-petalled flowers held above them
        for ( let x = -half( zm ); x < half( zm ); x += 0.22 ) {
          const [ lx, ly ] = P( x + r3() * 0.3, 0.45 + r3() * 0.2, zm - 1 + r3() * 2 );
          leafBlade( lx, ly, k3 * ( 0.28 + r3() * 0.14 ), -Math.PI / 2 + ( r3() - 0.5 ) * 2.2, inAir( this.mix( [ 56, 104, 48 ], [ 90, 140, 60 ], r3() ), zm ) );
        }
        for ( let x = -half( zm ); x < half( zm ); x += 0.75 + r3() * 0.4 ) {
          const col = inAir( flowers[ Math.floor( r3() * flowers.length ) ], zm ), [ hx, hy ] = P( x, 0.85 + r3() * 0.2, zm - 0.6 + r3() * 1.2 );
          for ( let q = 0; q < 6; q++ ) { flower( hx + ( r3() - 0.5 ) * k3 * 0.3, hy + ( r3() - 0.5 ) * k3 * 0.2, k3 * ( 0.09 + r3() * 0.04 ), col, r3() * 6 ); }
        }
      };
      // lane lines over Alcalá
      g.fillStyle = 'rgba(250,250,246,0.85)';
      // (kaizen 4) the arrows painted in the lanes, the near kerb's shade along the road's edge
      for ( const [ z, dir ] of [ [ 90, 1 ], [ 97, 1 ], [ 116, -1 ], [ 123, -1 ] ] as [ number, number ][] ) {
        for ( let x = -half( z ) + 30; x < half( z ); x += 70 ) {
          poly( [ P( x, 0.02, z - 0.15 ), P( x + dir * 3, 0.02, z - 0.15 ), P( x + dir * 3, 0.02, z + 0.15 ), P( x, 0.02, z + 0.15 ) ], 'rgba(250,250,246,0.7)' );
          poly( [ P( x + dir * 3, 0.02, z - 0.6 ), P( x + dir * 4.6, 0.02, z ), P( x + dir * 3, 0.02, z + 0.6 ) ], 'rgba(250,250,246,0.7)' );
        }
      }
      poly( [ P( -half( ALCALA.near ), 0.01, ALCALA.near ), P( half( ALCALA.near ), 0.01, ALCALA.near ), P( half( ALCALA.near + 1.2 ), 0.01, ALCALA.near + 1.2 ), P( -half( ALCALA.near + 1.2 ), 0.01, ALCALA.near + 1.2 ) ], 'rgba(40,40,52,0.14)' );
      for ( const z of [ 93.5, 119.5 ] ) {
        for ( let x = -half( z ); x < half( z ); x += 9 ) {
          const a = P( x, 0.02, z - 0.08 ), b = P( x + 4.5, 0.02, z + 0.08 );
          g.fillRect( a[ 0 ], b[ 1 ], b[ 0 ] - a[ 0 ], Math.max( 0.6, a[ 1 ] - b[ 1 ] ) );
        }
      }
      // (owner) no zebra crossing over Alcalá in the middle
      // Gran Vía's pavements on both sides and its centre line, going away
      const s0 = ALCALA.far / GV_DIR[ 1 ];
      for ( const [ v0, v1 ] of [ [ -GV_HALF, -GV_HALF + 5 ], [ GV_HALF - 5, GV_HALF ] ] ) {
        poly( [ P( ...this.gvxy( s0, v0 ) ), P( ...this.gvxy( 2000, v0 ) ), P( ...this.gvxy( 2000, v1 ) ), P( ...this.gvxy( s0, v1 ) ) ], this.rgba( inAir( [ 214, 206, 192 ], 300 ), 1 ) );
      }
      // (kaizen 17) the afternoon sun from the right lays the right side's shadow over half the avenue, cool blue
      {
        poly( [ P( ...this.gvxy( s0, GV_HALF ) ), P( ...this.gvxy( 1400, GV_HALF ) ), P( ...this.gvxy( 1400, -2 ) ), P( ...this.gvxy( s0 + 10, -2 ) ) ], 'rgba(40,56,96,0.16)' );
      }
      for ( let s = s0; s < 900; s += 12 ) {
        poly( [ P( ...this.gvxy( s, -0.15 ) ), P( ...this.gvxy( s + 6, -0.15 ) ), P( ...this.gvxy( s + 6, 0.15 ) ), P( ...this.gvxy( s, 0.15 ) ) ], 'rgba(250,250,246,0.8)' );
      }
    }

    // the buildings: oriented blocks, each with its fronts, cornices, rows of windows and a mansard; sorted far
    // to near with the rotunda and the trees
    interface Item { d: number; draw: () => void }
    const items: Item[] = [];
    const add = ( x: number, z: number, draw: () => void ) => items.push( { d: Math.hypot( x, z ), draw } );
    /**
     * A block: corner (x, z), u along `dir` for `len`, v back (to the left of dir) for `dep`, `h` high. Its fronts
     * in one of Madrid's styles (owner: the plain boxes were poor): white stone with pilasters and pedimented
     * windows, red brick dressed in stone with quoins, or warm ochre with glazed miradores; every one with a
     * rusticated ground floor of lit shops, a cornice on dentils, and a mansard with round dormers or a
     * balustrade with urns on its roof.
     */
    const block = ( x: number, z: number, dir: [ number, number ], len: number, dep: number, h: number, base: RGB, r: () => number, mansard = true, styled = true ) => {
      const [ dx, dz ] = dir, bx = -dz, bz = dx;
      const c = ( u: number, v: number, y: number ) => P( x + dx * u + bx * v, y, z + dz * u + bz * v );
      // each block its own seed from where it stands, so a change elsewhere never restyles it
      r = rr( Math.round( x * 31 + z * 17 ) + this.seedOfs );
      const pick = r();
      const style = styled ? ( pick < 0.4 ? 0 : pick < 0.7 ? 1 : 2 ) : 0;
      const wall: RGB = style === 1 ? this.mix( [ 176, 92, 70 ], red, 0.2 ) : style === 2 ? this.mix( [ 228, 192, 132 ], gold, 0.2 ) : base;
      const dressing: RGB = style === 0 ? this.mix( base, [ 255, 255, 250 ], 0.5 ) : [ 240, 234, 222 ];
      const roofKind = mansard && r() < 0.7 ? 0 : 1;
      // (owner: the left side's roofs wrong, the mirror of the right) a block built back with a negative depth
      // turns its long faces' normals too, or its far face is drawn as the front and the mansard leans out
      // (Alcalá's blocks only: Gran Vía's right row keeps the look the owner approved)
      const sg = dz === 0 ? Math.sign( dep ) || 1 : 1;
      const faces: [ number, number, number, number, number, number ][] = [ [ 0, 0, len, 0, -bx * sg, -bz * sg ], [ len, 0, len, dep, dx, dz ], [ len, dep, 0, dep, bx * sg, bz * sg ], [ 0, dep, 0, 0, -dx, -dz ] ];
      for ( const [ ua, va, ub, vb, nx, nz ] of faces ) {
        const fmx = x + dx * ( ua + ub ) / 2 + bx * ( va + vb ) / 2, fmz = z + dz * ( ua + ub ) / 2 + bz * ( va + vb ) / 2;
        if ( nx * -fmx + nz * -fmz <= 0 ) { continue; }
        const sk = sunK( nx, nz );
        const tone = ( col: RGB ) => faceCol( col, nx, nz, fmz );
        const col = tone( wall ), dress = tone( dressing );
        const wide = Math.hypot( ub - ua, vb - va );
        const at = ( t: number, y: number ) => c( ua + ( ub - ua ) * t, va + ( vb - va ) * t, y );
        const q4 = ( t0: number, t1: number, y0: number, y1: number, f: string | CanvasGradient ) => poly( [ at( t0, y0 ), at( t1, y0 ), at( t1, y1 ), at( t0, y1 ) ], f );
        const k = F / fmz;
        const n = Math.max( 1, Math.round( wide / 3.8 ) ), bay = 1 / n;
        const shadow = this.rgba( inAir( [ 70, 70, 86 ], fmz ), 0.22 + 0.15 * ( 1 - sk ) );
        // which way the shadows fall along this front: away from the sun on the right of the screen
        const rt = Math.sign( at( 1, 5 )[ 0 ] - at( 0, 5 )[ 0 ] ) || 1, shT = -rt;
        const outS = ( a0: number, a1: number, w: number ): [ number, number ] => shT > 0 ? [ a1, a1 + w ] : [ a0 - w, a0 ];
        const inS = ( a0: number, a1: number, w: number ): [ number, number ] => shT > 0 ? [ a0, a0 + w ] : [ a1 - w, a1 ];
        // the wall
        q4( 0, 1, 0, h, this.rgba( col, 1 ) );
        // the rusticated ground floor of stone and its shops: arched openings lit warm inside, awnings on some
        q4( 0, 1, 0, 4.8, this.rgba( tone( this.mix( dressing, [ 214, 206, 194 ], 0.4 ) ), 1 ) );
        if ( k > 0.3 ) {
          for ( let y = 0.7; y < 4.6; y += 0.7 ) { q4( 0, 1, y, y + 0.06, this.rgba( inAir( [ 120, 116, 112 ], fmz ), 0.35 ) ); }
          const awn: RGB[] = [ red, [ 40, 92, 70 ], this.mix( cream, gold, 0.3 ), [ 130, 36, 36 ] ];
          for ( let b = 0; b < n; b++ ) {
            const t0 = ( b + 0.16 ) * bay, t1 = ( b + 0.84 ) * bay, m = ( t0 + t1 ) / 2;
            const shop = g.createLinearGradient( 0, at( m, 0.3 )[ 1 ], 0, at( m, 3.9 )[ 1 ] );
            shop.addColorStop( 0, this.rgba( inAir( [ 236, 204, 150 ], fmz ), 1 ) );
            shop.addColorStop( 0.6, this.rgba( inAir( [ 150, 120, 96 ], fmz ), 1 ) );
            shop.addColorStop( 1, this.rgba( inAir( [ 60, 60, 68 ], fmz ), 1 ) );
            // (hourly 2) arched shopfronts on some buildings, square ones with a fascia on others
            if ( style !== 1 ) {
              const arch: [ number, number ][] = [ at( t0, 0.25 ), at( t1, 0.25 ), at( t1, 3.2 ) ];
              for ( let w = 1; w < 6; w++ ) { const th = w / 6 * Math.PI; arch.push( at( m + ( t1 - t0 ) / 2 * Math.cos( th ), 3.2 + 0.8 * Math.sin( th ) ) ); }
              arch.push( at( t0, 3.2 ) );
              poly( arch, shop );
            } else {
              q4( t0, t1, 0.25, 3.3, shop );
              q4( t0 - 0.02, t1 + 0.02, 3.3, 3.95, this.rgba( inAir( [ 44, 46, 52 ], fmz ), 1 ) );
            }
            if ( r() < 0.35 ) {
              // (hourly 8) the awning in stripes of its colour and white, its scalloped hem in shadow
              const ac = awn[ Math.floor( r() * awn.length ) ], stripes = 6;
              for ( let st = 0; st < stripes; st++ ) {
                const s0 = t0 - 0.01 + ( t1 - t0 + 0.02 ) * st / stripes, s1 = t0 - 0.01 + ( t1 - t0 + 0.02 ) * ( st + 1 ) / stripes;
                // (owner: 3px higher, then 1px back down) the awning raised a third of a metre
                q4( s0, s1, 3.33, 4.08, this.rgba( inAir( st % 2 ? [ 246, 242, 232 ] : ac, fmz ), 1 ) );
              }
              q4( t0 - 0.01, t1 + 0.01, 3.28, 3.38, this.rgba( inAir( this.mix( ac, [ 30, 20, 20 ], 0.4 ), fmz ), 1 ) );
            }
          }
        }
        // (hourly 9) the street's soft shade climbing the wall's foot
        if ( k > 0.3 ) {
          const ao = g.createLinearGradient( 0, at( 0.5, 0 )[ 1 ], 0, at( 0.5, 1.6 )[ 1 ] );
          ao.addColorStop( 0, 'rgba(60,60,72,0.28)' );
          ao.addColorStop( 1, 'rgba(60,60,72,0)' );
          q4( 0, 1, 0, 1.6, ao );
        }
        // the entresol's cornice with its shadow under it
        q4( 0, 1, 4.8, 5.5, this.rgba( dress, 1 ) );
        q4( 0, 1, 4.6, 4.8, shadow );
        // quoins for the brick fronts; pilasters between the bays for the stone ones
        if ( style === 1 ) {
          for ( let y = 5.5; y < h - 1.2; y += 1.1 ) {
            const wq = ( Math.round( y / 1.1 ) % 2 ? 1.4 : 0.9 ) / wide;
            q4( 0, wq, y, y + 0.9, this.rgba( dress, 1 ) );
            q4( 1 - wq, 1, y, y + 0.9, this.rgba( dress, 1 ) );
          }
        } else if ( style === 0 && k > 0.35 ) {
          for ( let b = 0; b <= n; b++ ) {
            const t = b * bay, pw = 0.55 / wide;
            q4( t - pw / 2, t + pw / 2, 5.5, h - 1.2, this.rgba( tone( this.mix( dressing, [ 255, 255, 255 ], 0.2 ) ), 1 ) );
            const [ ps0, ps1 ] = outS( t - pw / 2, t + pw / 2, 0.18 / wide );
            q4( ps0, ps1, 5.5, h - 1.2, shadow );
          }
        }
        // the floors above: framed windows, pediments over the first, balconies, glazed miradores on the ochre
        if ( k > 0.35 ) {
          const floors: number[] = [];
          for ( let fl = 6.4; fl < h - 3.2; fl += 3.6 ) { floors.push( fl ); }
          floors.forEach( ( fl, fi ) => {
            if ( fi > 0 ) { q4( 0, 1, fl - 0.55, fl - 0.35, this.rgba( dress, 0.9 ) ); }
            for ( let b = 0; b < n; b++ ) {
              const t0 = ( b + 0.3 ) * bay, t1 = ( b + 0.7 ) * bay, m = ( t0 + t1 ) / 2, e = 0.18 / wide;
              const mirador = style === 2 && ( b === 1 || b === n - 2 ) && fi > 0 && fi < floors.length - 1 && n > 3;
              if ( mirador ) {
                // a glazed bay standing out from the wall: its white frame, its panes, the mullions
                q4( t0 - 2.5 * e, t1 + 2.5 * e, fl - 0.4, fl + 3.1, this.rgba( tone( [ 248, 246, 240 ] ), 1 ) );
                const gl = g.createLinearGradient( 0, at( m, fl )[ 1 ], 0, at( m, fl + 2.7 )[ 1 ] );
                // (owner: no blue windows) the bay's panes in the same sunlit cream as the other windows
                gl.addColorStop( 0, this.rgba( inAir( [ 206, 194, 174 ], fmz ), 1 ) );
                gl.addColorStop( 0.5, this.rgba( inAir( [ 230, 220, 200 ], fmz ), 1 ) );
                gl.addColorStop( 1, this.rgba( inAir( [ 250, 246, 232 ], fmz ), 1 ) );
                q4( t0 - 1.8 * e, t1 + 1.8 * e, fl + 0.15, fl + 2.75, gl );
                for ( const tm of [ t0 + ( t1 - t0 ) * 0.33, t0 + ( t1 - t0 ) * 0.66 ] ) { q4( tm - 0.3 * e, tm + 0.3 * e, fl + 0.15, fl + 2.75, this.rgba( tone( [ 248, 246, 240 ] ), 1 ) ); }
                const [ ms0, ms1 ] = outS( t0 - 2.5 * e, t1 + 2.5 * e, 0.8 * e );
                q4( ms0, ms1, fl - 0.4, fl + 3.1, shadow );
                // (owner: windows missing) on to the next bay; a return here dropped the rest of the floor
                continue;
              }
              // (owner: the windows done well) a tall French window: its stone surround and shadow, the dark room
              // inside below, the sunlit street and sky held cream in its upper panes, the white frame with its
              // mullion and transom, the recess's shadow
              const w0 = ( b + 0.26 ) * bay, w1 = ( b + 0.74 ) * bay, wm = ( w0 + w1 ) / 2, top = fl + 2.85;
              q4( w0 - e, w1 + e, fl - 0.25, top + 0.3, this.rgba( dress, 1 ) );
              const [ ws0, ws1 ] = outS( w0 - e, w1 + e, 0.7 * e );
              q4( ws0, ws1, fl - 0.25, top + 0.3, shadow );
              const gl = g.createLinearGradient( 0, at( wm, fl )[ 1 ], 0, at( wm, top )[ 1 ] );
              // (owner: the glass creamier, its lower part too) sunlit cream from foot to head
              gl.addColorStop( 0, this.rgba( inAir( this.mix( [ 206, 194, 174 ], [ 172, 162, 148 ], 0.3 * ( 1 - sk ) ), fmz ), 1 ) );
              gl.addColorStop( 0.45, this.rgba( inAir( this.mix( [ 226, 216, 196 ], [ 192, 182, 166 ], 0.3 * ( 1 - sk ) ), fmz ), 1 ) );
              gl.addColorStop( 0.75, this.rgba( inAir( this.mix( [ 242, 234, 214 ], [ 210, 200, 184 ], 0.25 * ( 1 - sk ) ), fmz ), 1 ) );
              gl.addColorStop( 1, this.rgba( inAir( this.mix( [ 250, 246, 232 ], [ 222, 214, 198 ], 0.2 * ( 1 - sk ) ), fmz ), 1 ) );
              q4( w0, w1, fl, top, gl );
              if ( k > 0.55 ) {
                const frame = this.rgba( tone( [ 250, 248, 244 ] ), 1 ), fw = 0.09 / wide;
                q4( w0, w1, top - 0.08, top, frame );
                q4( w0, w0 + fw, fl, top, frame );
                q4( w1 - fw, w1, fl, top, frame );
                q4( wm - fw / 2, wm + fw / 2, fl, top, frame );
                q4( w0, w1, top - 0.75, top - 0.66, frame );
              }
              // (kaizen 2) Madrid's green shutters folded back beside some windows, a cream blind half down in others
              const leaf = r();
              if ( k > 0.5 && leaf < 0.18 ) {
                // (owner: the shutters the building's colour, a tone darker)
                const sh2 = this.rgba( tone( this.mix( wall, [ 70, 56, 44 ], 0.2 ) ), 1 ), lw = ( w1 - w0 ) * 0.42;
                q4( w0 - e - lw, w0 - e, fl - 0.1, top, sh2 );
                q4( w1 + e, w1 + e + lw, fl - 0.1, top, sh2 );
              } else if ( leaf > 0.78 ) {
                const bl = g.createLinearGradient( 0, at( wm, top )[ 1 ], 0, at( wm, top - 1.2 )[ 1 ] );
                bl.addColorStop( 0, this.rgba( tone( [ 236, 222, 192 ] ), 1 ) );
                bl.addColorStop( 1, this.rgba( tone( [ 214, 196, 160 ] ), 1 ) );
                q4( w0, w1, top - 1.2, top, bl );
              }
              const [ rs0, rs1 ] = inS( w0, w1, 0.2 / wide );
              q4( rs0, rs1, fl, top, shadow );
              q4( w0, w1, top - 0.2, top, shadow );
              // over the first floor's windows a pediment, triangular and curved by turns
              if ( fi === 0 ) {
                if ( b % 2 === 0 ) {
                  poly( [ at( w0 - 1.5 * e, top + 0.35 ), at( w1 + 1.5 * e, top + 0.35 ), at( wm, top + 1.0 ) ], this.rgba( dress, 1 ) );
                } else {
                  const seg: [ number, number ][] = [];
                  for ( let w = 0; w <= 6; w++ ) { const th = w / 6 * Math.PI; seg.push( at( wm + ( w1 - w0 + 3 * e ) / 2 * Math.cos( th ), top + 0.35 + 0.5 * Math.sin( th ) ) ); }
                  poly( seg, this.rgba( dress, 1 ) );
                }
                q4( w0 - 1.5 * e, w1 + 1.5 * e, top + 0.22, top + 0.35, shadow );
              }
              // the iron balconies: one long along the first floor, single ones on most windows above
              if ( fi === 0 || r() < 0.7 ) {
                // (owner: the window ironwork a darker cream)
                const iron = this.rgba( inAir( [ 168, 144, 106 ], fmz ), 0.95 ), b0 = w0 - 2 * e, b1 = w1 + 2 * e;
                q4( b0, b1, fl - 0.3, fl - 0.12, this.rgba( dress, 1 ) );
                q4( b0, b1, fl - 0.12, fl - 0.04, iron );
                if ( k > 0.5 ) { for ( let tb = b0; tb <= b1 + 1e-6; tb += ( b1 - b0 ) / 8 ) { q4( tb - 0.05 / wide, tb + 0.05 / wide, fl - 0.04, fl + 0.95, iron ); } }
                q4( b0, b1, fl + 0.9, fl + 1.0, iron );
                poly( [ at( b0, fl - 0.3 ), at( b1, fl - 0.3 ), at( b1 + shT * 0.35 / wide, fl - 0.9 ), at( b0 + shT * 0.35 / wide, fl - 0.9 ) ], shadow );
              }
            }
          } );
        }
        // the crowning cornice on its dentils, with its shadow, its top lit by the sun
        q4( 0, 1, h - 0.9, h, this.rgba( dress, 1 ) );
        q4( 0, 1, h - 0.15, h, this.rgba( tone( [ 255, 252, 244 ] ), 1 ) );
        // (kaizen 3) on some fronts a crowning ornament over the middle: a small segmental pediment on its
        // pedestal, finials at the corners
        if ( k > 0.4 && style !== 1 && wide > 18 && r() < 0.45 ) {
          const seg: [ number, number ][] = [];
          for ( let w = 0; w <= 8; w++ ) { const th = w / 8 * Math.PI; seg.push( at( 0.5 + 0.12 * Math.cos( th ), h + 0.3 + 1.6 * Math.sin( th ) ) ); }
          poly( [ at( 0.38, h ), at( 0.62, h ), at( 0.62, h + 0.4 ), at( 0.38, h + 0.4 ) ], this.rgba( dress, 1 ) );
          poly( seg, this.rgba( dress, 1 ) );
          for ( const t of [ 0.01, 0.99 ] ) {
            const [ fx, fy ] = at( t, h + 0.2 );
            g.fillStyle = this.rgba( dress, 1 );
            g.beginPath(); g.ellipse( fx, fy - k * 0.8, k * 0.35, k * 0.7, 0, 0, Math.PI * 2 ); g.fill();
            g.beginPath(); g.moveTo( fx - k * 0.1, fy - k * 1.4 ); g.lineTo( fx + k * 0.1, fy - k * 1.4 ); g.lineTo( fx, fy - k * 2 ); g.closePath(); g.fill();
          }
        }
        q4( 0, 1, h - 1.15, h - 0.9, shadow );
        q4( 0, 1, h - 1.75, h - 1.45, shadow );
        if ( k > 0.4 ) { for ( let t = 0; t < 1; t += 0.4 / wide ) { q4( t, t + 0.18 / wide, h - 1.45, h - 1.15, this.rgba( dress, 0.9 ) ); } }
        if ( roofKind === 0 ) {
          // the slate mansard leaning back, hipped at its ends so the corners close (owner: no gaps), its zinc
          // ridge catching the sky, small pedimented dormers along it
          const back = 2.4, mh = 4.2, tx = ( ub - ua ) / wide, tv = ( vb - va ) / wide;
          const wa = [ x + dx * ua + bx * va, z + dz * ua + bz * va ], wb = [ x + dx * ub + bx * vb, z + dz * ub + bz * vb ];
          const tX = dx * tx + bx * tv, tZ = dz * tx + bz * tv;
          const pa = P( wa[ 0 ], h, wa[ 1 ] ), pb = P( wb[ 0 ], h, wb[ 1 ] );
          const pa2 = P( wa[ 0 ] - nx * back + tX * back, h + mh, wa[ 1 ] - nz * back + tZ * back ), pb2 = P( wb[ 0 ] - nx * back - tX * back, h + mh, wb[ 1 ] - nz * back - tZ * back );
          const mg = g.createLinearGradient( 0, pa[ 1 ], 0, pa2[ 1 ] );
          mg.addColorStop( 0, this.rgba( inAir( this.mix( this.mix( slate, [ 150, 156, 170 ], 0.2 + sk * 0.35 ), [ 255, 236, 196 ], sk * 0.22 ), fmz ), 1 ) );
          mg.addColorStop( 0.85, this.rgba( inAir( this.mix( this.mix( slate, [ 176, 182, 196 ], 0.35 + sk * 0.3 ), [ 255, 240, 206 ], sk * 0.25 ), fmz ), 1 ) );
          mg.addColorStop( 1, this.rgba( inAir( [ 200, 208, 220 ], fmz ), 1 ) );
          poly( [ pa, pb, pb2, pa2 ], mg );
          // (hourly 3) a few chimney stacks standing on the roof behind
          for ( let q = 0; q < Math.max( 1, Math.round( wide / 14 ) ); q++ ) {
            const t = ( q + 0.3 + r() * 0.4 ) / Math.max( 1, Math.round( wide / 14 ) );
            const cxw = wa[ 0 ] + ( wb[ 0 ] - wa[ 0 ] ) * t - nx * ( back + 1.5 ), czw = wa[ 1 ] + ( wb[ 1 ] - wa[ 1 ] ) * t - nz * ( back + 1.5 );
            const c0 = P( cxw - 0.7, h + mh - 0.5, czw ), c1 = P( cxw + 0.7, h + mh + 2.2, czw );
            g.fillStyle = this.rgba( tone( this.mix( dressing, [ 200, 120, 90 ], 0.35 ) ), 1 );
            g.fillRect( c0[ 0 ], c1[ 1 ], Math.max( 0.6, c1[ 0 ] - c0[ 0 ] ), c0[ 1 ] - c1[ 1 ] );
            g.fillStyle = this.rgba( inAir( [ 70, 70, 80 ], fmz ), 0.8 );
            g.fillRect( c0[ 0 ] - ( c1[ 0 ] - c0[ 0 ] ) * 0.1, c1[ 1 ], ( c1[ 0 ] - c0[ 0 ] ) * 1.2, Math.max( 0.5, ( c0[ 1 ] - c1[ 1 ] ) * 0.12 ) );
          }
          if ( k > 0.4 ) {
            for ( let b = 1; b < n - ( n > 2 ? 1 : 0 ); b++ ) {
              const m = ( b + 0.5 ) * bay, d0 = m - 0.5 / wide, d1 = m + 0.5 / wide;
              poly( [ at( d0, h + 0.6 ), at( d1, h + 0.6 ), at( d1, h + 2.2 ), at( m, h + 2.9 ), at( d0, h + 2.2 ) ], this.rgba( dress, 1 ) );
              q4( d0 + 0.12 / wide, d1 - 0.12 / wide, h + 0.8, h + 2.05, this.rgba( inAir( [ 120, 116, 120 ], fmz ), 1 ) );
            }
          }
        } else {
          // a flat roof behind a balustrade with urns
          const [ ax, az ] = [ x + dx * ua + bx * va, z + dz * ua + bz * va ];
          balustrade( ax, az, [ ( ( x + dx * ub + bx * vb ) - ax ) / wide, ( ( z + dz * ub + bz * vb ) - az ) / wide ], wide, h );
        }
      }
    };
    const [ mx, mz ] = this.rotunda();
    const dGV = GV_DIR, dAL: [ number, number ] = [ -1, 0 ];
    // Gran Vía's left side beyond the Metropolis: block after block going away, white, cream and pale gold
    {
      const r = rr( 37 ), hues: RGB[] = [ stone, this.mix( stone, cream, 0.6 ), this.mix( stone, gold, 0.18 ), [ 236, 226, 214 ] ];
      const sStart = ALCALA.far / GV_DIR[ 1 ] + 52;
      for ( let s = sStart; s < 1100; ) {
        const len = 22 + r() * 22, h = 26 + r() * 10;
        // (owner: 3D) some fronts stand a little forward of their neighbours, so their ends show
        // (owner: never over the road) some fronts set a little back from the building line instead
        const back = r() < 0.4 ? 1.4 : 0;
        const [ x, z ] = this.gv( s, -GV_HALF - back );
        const base = hues[ Math.floor( r() * hues.length ) ];
        add( x, z + 10, () => block( x, z, dGV, len, 24 - back, h, base, r ) );
        s += len + ( r() < 0.25 ? 10 : 0 );
      }
      // the right side, the fronts facing the avenue
      // (owner: the corner building at the corner) the right side starts right at Alcalá's building line
      for ( let s = ALCALA.far / GV_DIR[ 1 ] + 6; s < 1100; ) {
        const len = 22 + r() * 24, h = 26 + r() * 12;
        const back = r() < 0.4 ? 1.4 : 0;
        const [ x, z ] = this.gv( s, GV_HALF + back );
        const base = hues[ Math.floor( r() * hues.length ) ];
        add( x, z + 10, () => block( x, z, dGV, len, -24 + back, h, base, r ) );
        // (kaizen 11) now and then a corner turret with its slate cupola, as along the photograph's avenue
        if ( r() < 0.22 ) {
          const tx = x + GV_DIR[ 0 ] * 3.2 + GV_DIR[ 1 ] * 3.2, tz = z + GV_DIR[ 1 ] * 3.2 - GV_DIR[ 0 ] * 3.2;
          add( x, z + 9.9, () => { cylinder( tx, tz, 3, h, h + 4.5, base, false ); dome( tx, tz, 3.3, h + 4.5, 4.2, slate, false ); } );
        }
        s += len + ( r() < 0.2 ? 10 : 0 );
      }
      // Alcalá's far side to the left of the Metropolis, and to the right beyond Gran Vía
      for ( let x = mx - 42; x > -1200; ) {
        const len = 26 + r() * 26, h = 24 + r() * 10, base = hues[ Math.floor( r() * hues.length ) ];
        const xx = x;
        add( xx - len / 2, ALCALA.far + 12, () => block( xx, ALCALA.far + 6, dAL, len, -26, h, base, r ) );
        x -= len;
      }
      const [ rx0 ] = this.gv( ALCALA.far / GV_DIR[ 1 ], GV_HALF );
      for ( let x = rx0 + 2; x < 1400; ) {
        const len = 26 + r() * 26, h = 26 + r() * 10, base = hues[ Math.floor( r() * hues.length ) ];
        const xx = x;
        add( xx + len / 2, ALCALA.far + 12, () => block( xx, ALCALA.far + 6, [ 1, 0 ], len, 26, h, base, r ) );
        x += len;
      }
    }
    // the Metropolis building's wings along both streets, under their mansards, its balustrade and statues
    {
      const r = rr( 41 );
      const [ gx0, gz0 ] = [ mx + GV_DIR[ 1 ] * ROT_R * 0.9, mz - GV_DIR[ 0 ] * ROT_R * 0.9 ];
      // the Gran Vía wing runs back from the rotunda along the avenue, its depth away to the left
      add( mx + 8, mz + 14, () => { block( gx0, gz0, dGV, 40, 24, 31, stone, r, true, false ); balustrade( gx0, gz0, dGV, 40, 31 ); } );
      // (owner: on a wide screen the Metropolis's lines end, then the next buildings start) a shorter wing
      add( mx - 20, mz + 10, () => { block( mx - 2, ALCALA.far + 6, dAL, 40, -24, 31, stone, r, true, false ); balustrade( mx - 2, ALCALA.far + 6, dAL, 40, 31 ); } );
    }
    // (kaizen 14) a balustrade along a front's cornice, with an urn on every pier
    const balustrade = ( x: number, z: number, dir: [ number, number ], len: number, h: number ) => {
      const at = ( u: number, y: number ) => P( x + dir[ 0 ] * u, y, z + dir[ 1 ] * u );
      const mz2 = z + dir[ 1 ] * len / 2, k = F / mz2;
      poly( [ at( 0, h ), at( len, h ), at( len, h + 1.3 ), at( 0, h + 1.3 ) ], this.rgba( inAir( this.mix( stone, [ 255, 255, 250 ], 0.3 ), mz2 ), 1 ) );
      for ( let u = 0.6; u < len; u += 0.7 ) { poly( [ at( u, h + 0.2 ), at( u + 0.28, h + 0.2 ), at( u + 0.28, h + 1 ), at( u, h + 1 ) ], this.rgba( inAir( [ 150, 154, 166 ], mz2 ), 0.6 ) ); }
      for ( let u = 3; u < len; u += 6 ) {
        const [ ux, uy ] = at( u, h + 1.3 );
        g.fillStyle = this.rgba( inAir( this.mix( stone, stoneShade, 0.25 ), mz2 ), 1 );
        g.beginPath(); g.ellipse( ux, uy - k * 0.7, k * 0.45, k * 0.6, 0, 0, Math.PI * 2 ); g.fill();
        g.fillRect( ux - k * 0.2, uy - k * 0.15, k * 0.4, k * 0.3 );
        g.beginPath(); g.arc( ux, uy - k * 1.4, k * 0.18, 0, Math.PI * 2 ); g.fill();
      }
    };
    // the Grassy building's round corner up Gran Vía, its little colonnaded temple and cupola on top
    {
      const [ gx, gz ] = this.gv( ALCALA.far / GV_DIR[ 1 ] + 46, -GV_HALF + 6 );
      add( gx, gz, () => {
        cylinder( gx, gz, 6, 0, 28, stone, true );
        // (kaizen 15) its balconied belvedere: a ring of slim columns open to the sky, a cornice, the cupola
        cylinder( gx, gz, 5, 28, 29, this.mix( stone, [ 255, 255, 250 ], 0.3 ), false );
        cylinder( gx, gz, 4.2, 29, 33.5, stone, false, true );
        cylinder( gx, gz, 4.6, 33.2, 34, this.mix( stone, [ 255, 255, 250 ], 0.3 ), false );
        dome( gx, gz, 4.4, 34, 3.4, [ 70, 84, 110 ], false );
      } );
    }
    // (kaizen) a window on a drum's face at angle a, `hw` radians half wide: its stone surround with the
    // shadow at its right, the dark room below and the sunlit cream glass above, the frame, an arched head
    // if asked, a balcony if asked
    const drumWindow = ( cx: number, cz: number, rad: number, a: number, hw: number, y0: number, y1: number, arched: boolean, balcony: boolean, base: RGB ) => {
      const pt = ( aa: number, y: number, rr2 = rad ) => P( cx + Math.sin( aa ) * rr2, y, cz - Math.cos( aa ) * rr2 );
      const nx = Math.sin( a ), nz = -Math.cos( a ), pz = cz + nz * rad, sk = sunK( nx, nz );
      const dress = this.rgba( faceCol( this.mix( base, [ 255, 255, 250 ], 0.5 ), nx, nz, pz ), 1 );
      const shadow = this.rgba( inAir( [ 70, 70, 86 ], pz ), 0.25 + 0.15 * ( 1 - sk ) );
      const head = ( r2: number, w: number, yTop: number ) => {
        const pts: [ number, number ][] = [ pt( a - w, y0, r2 ), pt( a + w, y0, r2 ), pt( a + w, yTop, r2 ) ];
        if ( arched ) { for ( let q = 1; q < 8; q++ ) { const th = q / 8 * Math.PI; pts.push( pt( a + w * Math.cos( th ), yTop + w * rad * 0.9 * Math.sin( th ), r2 ) ); } }
        pts.push( pt( a - w, yTop, r2 ) );
        return pts;
      };
      poly( head( rad + 0.05, hw * 1.35, y1 + 0.2 ), dress );
      poly( [ pt( a - hw * 1.6, y0, rad + 0.04 ), pt( a - hw * 1.35, y0, rad + 0.04 ), pt( a - hw * 1.35, y1 + 0.2, rad + 0.04 ), pt( a - hw * 1.6, y1 + 0.2, rad + 0.04 ) ], shadow );
      const [ , ya ] = pt( a, y0 ), [ , yb ] = pt( a, y1 );
      const gl = g.createLinearGradient( 0, ya, 0, yb );
      gl.addColorStop( 0, this.rgba( inAir( [ 206, 194, 174 ], pz ), 1 ) );
      gl.addColorStop( 0.5, this.rgba( inAir( [ 230, 220, 200 ], pz ), 1 ) );
      gl.addColorStop( 1, this.rgba( inAir( this.mix( [ 246, 238, 220 ], [ 214, 204, 188 ], 0.3 * ( 1 - sk ) ), pz ), 1 ) );
      poly( head( rad + 0.08, hw, y1 ), gl );
      poly( [ pt( a - hw * 0.08, y0, rad + 0.1 ), pt( a + hw * 0.08, y0, rad + 0.1 ), pt( a + hw * 0.08, y1, rad + 0.1 ), pt( a - hw * 0.08, y1, rad + 0.1 ) ], dress );
      poly( [ pt( a + hw * 0.75, y0, rad + 0.1 ), pt( a + hw, y0, rad + 0.1 ), pt( a + hw, y1, rad + 0.1 ), pt( a + hw * 0.75, y1, rad + 0.1 ) ], shadow );
      if ( balcony ) {
        const iron = this.rgba( inAir( [ 168, 144, 106 ], pz ), 0.95 );
        poly( [ pt( a - hw * 1.6, y0 - 0.3, rad + 0.5 ), pt( a + hw * 1.6, y0 - 0.3, rad + 0.5 ), pt( a + hw * 1.6, y0 - 0.1, rad + 0.5 ), pt( a - hw * 1.6, y0 - 0.1, rad + 0.5 ) ], dress );
        poly( [ pt( a - hw * 1.6, y0 + 0.8, rad + 0.5 ), pt( a + hw * 1.6, y0 + 0.8, rad + 0.5 ), pt( a + hw * 1.6, y0 + 0.9, rad + 0.5 ), pt( a - hw * 1.6, y0 + 0.9, rad + 0.5 ) ], iron );
        for ( let q = 0; q <= 6; q++ ) { const aa = a - hw * 1.6 + hw * 3.2 * q / 6; poly( [ pt( aa - hw * 0.05, y0 - 0.1, rad + 0.5 ), pt( aa + hw * 0.05, y0 - 0.1, rad + 0.5 ), pt( aa + hw * 0.05, y0 + 0.85, rad + 0.5 ), pt( aa - hw * 0.05, y0 + 0.85, rad + 0.5 ) ], iron ); }
        poly( [ pt( a - hw * 1.6, y0 - 0.3, rad + 0.05 ), pt( a + hw * 1.6, y0 - 0.3, rad + 0.05 ), pt( a + hw * 1.3, y0 - 0.9, rad + 0.05 ), pt( a - hw * 1.9, y0 - 0.9, rad + 0.05 ) ], shadow );
      }
    };
    // a column drum: lit strips round its visible half, cornices round it, windows in every bay
    const cylinder = ( cx: number, cz: number, rad: number, y0: number, y1: number, base: RGB, floors: boolean, open = false ) => {
      const N = 48;
      const pt = ( a: number, y: number, rr2 = rad ) => P( cx + Math.sin( a ) * rr2, y, cz - Math.cos( a ) * rr2 );
      for ( let q = 0; q < N; q++ ) {
        const a0 = q / N * Math.PI * 2, a1 = ( q + 1 ) / N * Math.PI * 2, am = ( a0 + a1 ) / 2;
        const nx = Math.sin( am ), nz = -Math.cos( am );
        const px = cx + nx * rad, pz = cz + nz * rad;
        if ( nx * -px + nz * -pz <= 0 ) { continue; }
        const col = faceCol( base, nx, nz, pz );
        poly( [ pt( a0, y0 ), pt( a1, y0 ), pt( a1, y1 ), pt( a0, y1 ) ], this.rgba( col, 1 ) );
        if ( open && q % 3 === 0 ) { poly( [ pt( a0, y0 + 0.8 ), pt( a0 + ( a1 - a0 ) * 2.2, y0 + 0.8 ), pt( a0 + ( a1 - a0 ) * 2.2, y1 - 1 ), pt( a0, y1 - 1 ) ], this.rgba( inAir( [ 248, 234, 190 ], pz ), 0.9 ) ); }
        if ( floors && q % 3 === 1 ) {
          for ( let fl = 6; fl < y1 - 3; fl += 4 ) { drumWindow( cx, cz, rad, am + ( a1 - a0 ) * 0.2, 0.75 / rad, fl, fl + 2.6, fl > y1 - 8, true, base ); }
        }
      }
      // its cornices: light rings
      for ( const cyy of floors ? [ y0 + 4.5, y1 - 0.8 ] : [ y1 - 0.6 ] ) {
        for ( let q = 0; q < N; q++ ) {
          const a0 = q / N * Math.PI * 2, a1 = ( q + 1 ) / N * Math.PI * 2, am = ( a0 + a1 ) / 2;
          const nx = Math.sin( am ), nz = -Math.cos( am ), px = cx + nx * rad, pz = cz + nz * rad;
          if ( nx * -px + nz * -pz <= 0 ) { continue; }
          poly( [ pt( a0, cyy, rad + 0.35 ), pt( a1, cyy, rad + 0.35 ), pt( a1, cyy + 0.7, rad + 0.35 ), pt( a0, cyy + 0.7, rad + 0.35 ) ], this.rgba( this.mix( faceCol( base, nx, nz, pz ), [ 255, 255, 250 ], 0.35 ), 1 ) );
        }
      }
    };
    // a dome: an ogee of slate rising from its drum to a point, lit at its left, dressed in gold
    const dome = ( cx: number, cz: number, rad: number, y0: number, hgt: number, col: RGB, gilt: boolean ) => {
      const N = 40, M2 = 14;
      const radAt = ( t: number ) => rad * Math.pow( 1 - t, 0.55 ) * ( 1 + 0.1 * Math.sin( Math.PI * t ) );
      const pt = ( a: number, t: number ) => { const rr2 = radAt( t ); return P( cx + Math.sin( a ) * rr2, y0 + hgt * t, cz - Math.cos( a ) * rr2 ); };
      for ( let j = 0; j < M2; j++ ) {
        const t0 = j / M2, t1 = ( j + 1 ) / M2;
        for ( let q = 0; q < N; q++ ) {
          const a0 = q / N * Math.PI * 2, a1 = ( q + 1 ) / N * Math.PI * 2, am = ( a0 + a1 ) / 2;
          const nx = Math.sin( am ), nz = -Math.cos( am ), px = cx + nx * rad, pz = cz + nz * rad;
          if ( nx * -px + nz * -pz <= 0 ) { continue; }
          const tilt = 0.35 + 0.65 * ( 1 - ( t0 + t1 ) / 2 );
          const k = sunK( nx * tilt, nz * tilt ) * 0.8 + ( 1 - tilt ) * 0.3;
          poly( [ pt( a0, t0 ), pt( a1, t0 ), pt( a1, t1 ), pt( a0, t1 ) ], this.rgba( inAir( this.mix( col, [ 170, 180, 200 ], k * 0.55 ), pz, 0.6 ), 1 ) );
        }
      }
      if ( !gilt ) { return; }
      // the gold: a garland band round the lower dome, ribs up it, a crown ring near the top, a round window
      // (owner: the gold shining a little) brighter gilt, catching the sun on its right
      const goldC = ( k: number ) => this.rgba( this.mix( this.mix( gold, amber, 0.2 ), [ 255, 246, 200 ], Math.min( 1, 0.25 + k * 0.9 ) ), 1 );
      for ( let q = 0; q < N; q++ ) {
        const a0 = q / N * Math.PI * 2, a1 = ( q + 1 ) / N * Math.PI * 2, am = ( a0 + a1 ) / 2;
        const nx = Math.sin( am ), nz = -Math.cos( am ), px = cx + nx * rad, pz = cz + nz * rad;
        if ( nx * -px + nz * -pz <= 0 ) { continue; }
        const k = sunK( nx, nz ) * 0.6;
        for ( const [ ta, tb ] of [ [ 0.02, 0.09 ], [ 0.42, 0.47 ], [ 0.86, 0.95 ] ] ) { poly( [ pt( a0, ta ), pt( a1, ta ), pt( a1, tb ), pt( a0, tb ) ], goldC( k ) ); }
        // the garland swags hanging between the ribs
        if ( q % 5 === 2 ) {
          const sw: [ number, number ][] = [];
          for ( let w = 0; w <= 6; w++ ) { const aw = a0 - ( a1 - a0 ) * 2 + ( a1 - a0 ) * 5 * w / 6; sw.push( pt( aw, 0.47 - 0.07 * Math.sin( Math.PI * w / 6 ) ) ); }
          g.strokeStyle = goldC( k );
          g.lineWidth = Math.max( 0.6, F / pz * 0.28 );
          g.beginPath(); sw.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.stroke();
        }
        if ( q % 5 === 0 ) {
          g.strokeStyle = goldC( k );
          g.lineWidth = Math.max( 0.6, F / pz * 0.22 );
          g.beginPath();
          for ( let j = 0; j <= 12; j++ ) { const [ x, y ] = pt( a0, 0.09 + 0.77 * j / 12 ); j ? g.lineTo( x, y ) : g.moveTo( x, y ); }
          g.stroke();
        }
      }
      // the round window toward us, framed in gold
      const aFront = Math.atan2( -cx, cz );
      const [ ox, oy ] = pt( aFront, 0.28 ), k = F / cz;
      g.fillStyle = goldC( 0.4 );
      g.beginPath(); g.ellipse( ox, oy, k * 1.2, k * 1.1, 0, 0, Math.PI * 2 ); g.fill();
      g.fillStyle = 'rgb(40,44,56)';
      g.beginPath(); g.ellipse( ox, oy, k * 0.75, k * 0.7, 0, 0, Math.PI * 2 ); g.fill();
    };
    // the Metropolis rotunda: three storeys of white stone with paired columns, sculpture groups on its top
    // cornice, the black METROPOLIS band on its drum, the gilded dome and the winged Victory
    add( mx, mz - 2, () => {
      const r = rr( 47 );
      cylinder( mx, mz, ROT_R, 0, 12.4, this.mix( stone, [ 220, 216, 206 ], 0.3 ), false );
      // the ground floor's rustication and its arched windows
      const N = 48, pt = ( a: number, y: number, rad = ROT_R ) => P( mx + Math.sin( a ) * rad, y, mz - Math.cos( a ) * rad );
      const vis = ( a: number, rad = ROT_R ) => { const nx = Math.sin( a ), nz = -Math.cos( a ); return nx * -( mx + nx * rad ) + nz * -( mz + nz * rad ) > 0; };
      for ( let y = 1.2; y < 11; y += 1.1 ) {
        for ( let q = 0; q < N; q++ ) {
          const a0 = q / N * Math.PI * 2, a1 = ( q + 1 ) / N * Math.PI * 2;
          if ( !vis( ( a0 + a1 ) / 2 ) ) { continue; }
          poly( [ pt( a0, y, ROT_R + 0.05 ), pt( a1, y, ROT_R + 0.05 ), pt( a1, y + 0.12, ROT_R + 0.05 ), pt( a0, y + 0.12, ROT_R + 0.05 ) ], 'rgba(120,118,120,0.35)' );
        }
      }
      for ( let q = 0; q < 8; q++ ) {
        const a = q / 8 * Math.PI * 2 + 0.2;
        if ( !vis( a ) ) { continue; }
        // (kaizen 13) a tall arched opening: the shop's glass warm inside below, cool above, its keystone
        const arch: [ number, number ][] = [ pt( a - 0.13, 1.2, ROT_R + 0.06 ), pt( a + 0.13, 1.2, ROT_R + 0.06 ), pt( a + 0.13, 8.4, ROT_R + 0.06 ) ];
        for ( let w = 1; w < 8; w++ ) { const t = w / 8; arch.push( pt( a + 0.13 * Math.cos( Math.PI * t ), 8.4 + 1.3 * Math.sin( Math.PI * t ), ROT_R + 0.06 ) ); }
        arch.push( pt( a - 0.13, 8.4, ROT_R + 0.06 ) );
        const top = pt( a, 9.7, ROT_R + 0.06 ), bot = pt( a, 1.2, ROT_R + 0.06 );
        const ag = g.createLinearGradient( 0, bot[ 1 ], 0, top[ 1 ] );
        ag.addColorStop( 0, 'rgba(226,190,140,0.95)' );
        ag.addColorStop( 0.35, 'rgba(120,104,96,0.95)' );
        ag.addColorStop( 1, 'rgba(70,80,100,0.95)' );
        poly( arch, ag );
        poly( [ pt( a - 0.13, 3.6, ROT_R + 0.08 ), pt( a + 0.13, 3.6, ROT_R + 0.08 ), pt( a + 0.13, 4.3, ROT_R + 0.08 ), pt( a - 0.13, 4.3, ROT_R + 0.08 ) ], this.rgba( red, 1 ) );
        poly( [ pt( a - 0.03, 9.5, ROT_R + 0.1 ), pt( a + 0.03, 9.5, ROT_R + 0.1 ), pt( a + 0.02, 10.3, ROT_R + 0.1 ), pt( a - 0.02, 10.3, ROT_R + 0.1 ) ], 'rgba(250,250,246,1)' );
      }
      // the main storeys
      cylinder( mx, mz, ROT_R - 0.4, 12.4, 25, stone, false );
      for ( let q = 0; q < 16; q++ ) {
        const a = q / 16 * Math.PI * 2;
        if ( !vis( a, ROT_R - 0.4 ) ) { continue; }
        const nx = Math.sin( a ), nz = -Math.cos( a );
        if ( q % 2 === 0 ) {
          // a pair of columns standing proud, lit at their left, their shadow on the wall
          // (kaizen) each column round: its lit left half and shaded right, its base and capital standing out,
          // its shadow cast on the wall at its right
          for ( const da of [ -0.04, 0.04 ] ) {
            const k = sunK( nx, nz ), rc = ROT_R + 0.45;
            poly( [ pt( a + da - 0.065, 13, ROT_R - 0.35 ), pt( a + da - 0.026, 13, ROT_R - 0.35 ), pt( a + da - 0.026, 23.6, ROT_R - 0.35 ), pt( a + da - 0.065, 23.6, ROT_R - 0.35 ) ], 'rgba(100,104,124,0.42)' );
            poly( [ pt( a + da, 13.4, rc ), pt( a + da + 0.026, 13.4, rc ), pt( a + da + 0.026, 23.2, rc ), pt( a + da, 23.2, rc ) ], this.rgba( this.mix( stoneShade, [ 255, 253, 248 ], 0.35 + 0.65 * k ), 1 ) );
            poly( [ pt( a + da - 0.026, 13.4, rc ), pt( a + da, 13.4, rc ), pt( a + da, 23.2, rc ), pt( a + da - 0.026, 23.2, rc ) ], this.rgba( this.mix( stoneShade, [ 230, 230, 232 ], 0.25 * k ), 1 ) );
            for ( const [ y0, y1 ] of [ [ 12.9, 13.4 ], [ 23.2, 23.9 ] ] ) {
              poly( [ pt( a + da - 0.036, y0, rc + 0.05 ), pt( a + da + 0.036, y0, rc + 0.05 ), pt( a + da + 0.036, y1, rc + 0.05 ), pt( a + da - 0.036, y1, rc + 0.05 ) ], this.rgba( this.mix( stoneShade, [ 255, 253, 248 ], 0.5 + 0.5 * k ), 1 ) );
            }
          }
        } else {
          // the windows between them: two storeys, the lower with its balcony, the upper arched
          drumWindow( mx, mz, ROT_R - 0.4, a, 0.075, 14.4, 17.9, false, true, stone );
          drumWindow( mx, mz, ROT_R - 0.4, a, 0.07, 19.6, 22.4, true, false, stone );
        }
      }
      // the great cornice over the columns, the attic with the sculpture groups, the drum and its band
      cylinder( mx, mz, ROT_R + 0.3, 24.6, 25.8, this.mix( stone, [ 255, 255, 250 ], 0.3 ), false );
      cylinder( mx, mz, ROT_R - 0.9, 25.8, 31.5, stone, false );
      for ( let q = 0; q < 8; q++ ) {
        const a = q / 8 * Math.PI * 2 + Math.PI / 8;
        if ( !vis( a ) ) { continue; }
        // a sculpture group: seated figures, a soft grey heap of shapes against the attic
        const nx = Math.sin( a ), nz = -Math.cos( a ), k = sunK( nx, nz );
        // (kaizen 6) a group of three seated figures on a plinth: each a body leaning back, its knees, a
        // head, lit at the left; the middle one raised
        const s = F / mz, litC = this.rgba( this.mix( stoneShade, [ 252, 250, 244 ], 0.45 + 0.55 * k ), 1 ), shC = this.rgba( this.mix( stoneShade, [ 200, 200, 206 ], 0.2 ), 1 );
        const [ bx0, by0 ] = pt( a - 0.11, 26.0, ROT_R + 0.25 ), [ bx1 ] = pt( a + 0.11, 26.0, ROT_R + 0.25 );
        g.fillStyle = litC;
        g.fillRect( bx0, by0 - s * 0.5, bx1 - bx0, s * 0.5 );
        for ( let f = 0; f < 3; f++ ) {
          const da = ( f - 1 ) * 0.075, lift = f === 1 ? 0.9 : 0, [ fx, fy ] = pt( a + da, 26.5 + lift, ROT_R + 0.3 );
          g.fillStyle = shC;
          g.beginPath(); g.ellipse( fx + s * 0.18, fy - s * 0.9, s * 0.32, s * 0.85, 0.15, 0, Math.PI * 2 ); g.fill();
          g.fillStyle = litC;
          g.beginPath(); g.ellipse( fx, fy - s * 0.95, s * 0.3, s * 0.85, -0.15, 0, Math.PI * 2 ); g.fill();
          g.beginPath(); g.ellipse( fx + s * ( f === 0 ? 0.35 : -0.35 ), fy - s * 0.25, s * 0.45, s * 0.22, 0, 0, Math.PI * 2 ); g.fill();
          g.beginPath(); g.arc( fx, fy - s * 2.0, s * 0.27, 0, Math.PI * 2 ); g.fill();
        }
      }
      cylinder( mx, mz, ROT_R - 1.4, 31.5, 34.6, this.mix( stone, [ 225, 222, 214 ], 0.4 ), false );
      {
        // (owner: the name was a font and ran over its band) the black plaque round the drum with the name
        // drawn letter by letter in gold strokes, each letter set on the drum's curve, the plaque sized to them
        const O: [ number, number ][] = [];
        for ( let w = 0; w <= 14; w++ ) { const a = w / 14 * Math.PI * 2; O.push( [ 0.31 + Math.sin( a ) * 0.29, 0.5 + Math.cos( a ) * 0.5 ] ); }
        const PB: [ number, number ][] = [ [ 0, 0 ], [ 0, 1 ], [ 0.42, 1 ], [ 0.57, 0.88 ], [ 0.57, 0.63 ], [ 0.42, 0.5 ], [ 0, 0.5 ] ];
        const GLYPH: Record<string, { w: number; s: [ number, number ][][] }> = {
          M: { w: 0.66, s: [ [ [ 0, 0 ], [ 0, 1 ], [ 0.33, 0.42 ], [ 0.66, 1 ], [ 0.66, 0 ] ] ] },
          E: { w: 0.55, s: [ [ [ 0.55, 1 ], [ 0, 1 ], [ 0, 0 ], [ 0.55, 0 ] ], [ [ 0, 0.5 ], [ 0.44, 0.5 ] ] ] },
          T: { w: 0.62, s: [ [ [ 0, 1 ], [ 0.62, 1 ] ], [ [ 0.31, 1 ], [ 0.31, 0 ] ] ] },
          R: { w: 0.6, s: [ PB, [ [ 0.3, 0.5 ], [ 0.6, 0 ] ] ] },
          O: { w: 0.62, s: [ O ] },
          P: { w: 0.58, s: [ PB ] },
          L: { w: 0.54, s: [ [ [ 0, 1 ], [ 0, 0 ], [ 0.54, 0 ] ] ] },
          I: { w: 0.1, s: [ [ [ 0.05, 1 ], [ 0.05, 0 ] ] ] },
          S: { w: 0.6, s: [ [ [ 0.58, 0.9 ], [ 0.45, 1 ], [ 0.13, 1 ], [ 0.01, 0.86 ], [ 0.01, 0.64 ], [ 0.13, 0.53 ], [ 0.47, 0.47 ], [ 0.6, 0.36 ], [ 0.6, 0.13 ], [ 0.47, 0 ], [ 0.13, 0 ], [ 0, 0.1 ] ] ] },
        };
        const word = 'METROPOLIS', capH = 0.92, gap = 0.26, Rd = ROT_R - 1.25;
        const span = word.split( '' ).reduce( ( a, ch ) => a + GLYPH[ ch ].w + gap, -gap ) * capH;
        const aF = Math.atan2( -mx, mz ), padA = 0.55 / Rd, a0 = aF - span / 2 / Rd;
        const plaque: [ number, number ][] = [];
        for ( let w = 0; w <= 16; w++ ) { plaque.push( pt( a0 - padA + ( span / Rd + 2 * padA ) * w / 16, 32.2, Rd ) ); }
        for ( let w = 16; w >= 0; w-- ) { plaque.push( pt( a0 - padA + ( span / Rd + 2 * padA ) * w / 16, 34.0, Rd ) ); }
        poly( plaque, 'rgb(28,28,34)' );
        const goldLine = this.rgba( this.mix( gold, [ 255, 238, 190 ], 0.35 ), 1 ), k = F / mz;
        g.strokeStyle = goldLine;
        g.lineJoin = 'round';
        g.lineCap = 'round';
        g.lineWidth = Math.max( 0.5, k * 0.07 );
        g.beginPath(); plaque.forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.closePath(); g.stroke();
        g.lineWidth = Math.max( 0.6, k * 0.11 );
        let u = 0;
        for ( const ch of word ) {
          const gl = GLYPH[ ch ];
          for ( const stroke of gl.s ) {
            g.beginPath();
            stroke.forEach( ( [ gx, gy ], q ) => {
              const [ x, y ] = pt( a0 + ( u + gx ) * capH / Rd, 32.64 + gy * capH, Rd + 0.02 );
              q ? g.lineTo( x, y ) : g.moveTo( x, y );
            } );
            g.stroke();
          }
          u += gl.w + gap;
        }
      }
      void r;
      dome( mx, mz, ROT_R - 1.1, 34.6, 10.9, [ 26, 28, 40 ], true );
      // the lantern and the winged Victory, dark bronze with the sun on her wings
      // (kaizen 7) the gilded crown at the dome's top: a ring of gold leaves and the lantern under the Victory
      cylinder( mx, mz, 1.6, 44.8, 45.4, this.mix( gold, [ 255, 244, 196 ], 0.5 ), false );
      for ( let q = 0; q < 12; q++ ) {
        const a = q / 12 * Math.PI * 2, nx = Math.sin( a ), nz = -Math.cos( a );
        if ( nx * -( mx + nx * 1.6 ) + nz * -( mz + nz * 1.6 ) <= 0 ) { continue; }
        const [ lx, ly ] = P( mx + nx * 1.6, 45.4, mz + nz * 1.6 ), s = F / mz;
        g.fillStyle = this.rgba( this.mix( gold, [ 255, 240, 190 ], 0.3 ), 1 );
        g.beginPath(); g.moveTo( lx - s * 0.16, ly ); g.lineTo( lx, ly - s * 0.6 ); g.lineTo( lx + s * 0.16, ly ); g.closePath(); g.fill();
      }
      cylinder( mx, mz, 0.9, 45.4, 46.6, this.mix( gold, [ 255, 240, 190 ], 0.35 ), false );
      // the sun's glints on the gilt: small bright points on the crown's and the lantern's sunlit side
      {
        const s = F / mz;
        for ( const [ a, y, rr2, sz ] of [ [ 0.5, 45.1, 1.65, 0.28 ], [ 0.9, 45.0, 1.65, 0.2 ], [ 0.45, 46.0, 0.95, 0.24 ], [ 0.35, 44.2, 2.2, 0.22 ] ] ) {
          const [ gx, gy ] = P( mx + Math.sin( a ) * rr2, y, mz - Math.cos( a ) * rr2 );
          const gl = g.createRadialGradient( gx, gy, 0, gx, gy, s * sz * 2.4 );
          gl.addColorStop( 0, 'rgba(255,255,240,0.95)' );
          gl.addColorStop( 0.4, 'rgba(255,240,180,0.45)' );
          gl.addColorStop( 1, 'rgba(255,230,160,0)' );
          g.fillStyle = gl;
          g.fillRect( gx - s * sz * 2.4, gy - s * sz * 2.4, s * sz * 4.8, s * sz * 4.8 );
        }
      }
      const [ sx, sy ] = P( mx, 46.6, mz ), s = F / mz, bronze = 'rgb(42,40,40)', litB = 'rgb(118,104,84)';
      g.fillStyle = bronze;
      g.beginPath();
      g.moveTo( sx - s * 0.35, sy ); g.lineTo( sx + s * 0.35, sy ); g.lineTo( sx + s * 0.25, sy - s * 2.4 ); g.lineTo( sx + s * 0.1, sy - s * 3.3 );
      g.lineTo( sx - s * 0.1, sy - s * 3.3 ); g.lineTo( sx - s * 0.25, sy - s * 2.4 ); g.closePath(); g.fill();
      g.beginPath(); g.arc( sx, sy - s * 3.55, s * 0.26, 0, Math.PI * 2 ); g.fill();
      // her arm raised with the laurel, her wings spread up and out
      g.fillRect( sx + s * 0.15, sy - s * 4.4, s * 0.14, s * 1.4 );
      for ( const sd of [ -1, 1 ] ) {
        g.fillStyle = sd < 0 ? litB : bronze;
        g.beginPath();
        g.moveTo( sx + sd * s * 0.15, sy - s * 2.9 );
        g.quadraticCurveTo( sx + sd * s * 1.6, sy - s * 4.2, sx + sd * s * 2.7, sy - s * 4.6 );
        g.lineTo( sx + sd * s * 2.3, sy - s * 3.9 );
        g.lineTo( sx + sd * s * 2.5, sy - s * 3.5 );
        g.lineTo( sx + sd * s * 1.9, sy - s * 3.1 );
        g.quadraticCurveTo( sx + sd * s * 0.9, sy - s * 2.6, sx + sd * s * 0.15, sy - s * 2.4 );
        g.closePath(); g.fill();
      }
    } );
    // the plane trees along Alcalá before the Metropolis's left wing: full summer crowns, lit at their left
    {
      const r = rr( 53 );
      for ( let x = mx - 18; x > -900; x -= 11 + r() * 8 ) {
        const z = ALCALA.far + 2 + r() * 3, hgt = 13 + r() * 7, wide = 5.5 + r() * 3;
        add( x, z - 30, () => tree( x, z, hgt, wide, r ) );
      }
      // (kaizen 16) and along Alcalá beyond the avenue's mouth on the right
      const [ rx1 ] = this.gv( ALCALA.far / GV_DIR[ 1 ], GV_HALF );
      for ( let x = rx1 + 52; x < 1200; x += 13 + r() * 8 ) {
        const z = ALCALA.far + 2 + r() * 3, hgt = 12 + r() * 5, wide = 4.5 + r() * 2;
        add( x, z - 30, () => tree( x, z, hgt, wide, r ) );
      }
    }
    // (owner: the trees redrawn with quality, the greens above all) leaf sprites in three turns, a pointed
    // leaf with its lit edge, painted once per tone
    const leafCache = new Map<string, HTMLCanvasElement>();
    const leaf = ( tint: RGB, turn: number ) => {
      const key = tint.map( v => Math.round( v / 5 ) ).join( ',' ) + ':' + turn;
      const hit = leafCache.get( key );
      if ( hit ) { return hit; }
      const size = 24, cv = document.createElement( 'canvas' );
      cv.width = cv.height = size;
      const lg = cv.getContext( '2d' )!;
      lg.translate( size / 2, size / 2 );
      lg.rotate( -0.6 + turn * 0.9 );
      const fg = lg.createLinearGradient( -size * 0.4, -size * 0.3, size * 0.4, size * 0.3 );
      fg.addColorStop( 0, this.rgba( this.mix( tint, [ 255, 255, 220 ], 0.16 ), 1 ) );
      fg.addColorStop( 1, this.rgba( this.mix( tint, [ 10, 30, 20 ], 0.12 ), 1 ) );
      lg.fillStyle = fg;
      lg.beginPath();
      lg.moveTo( -size * 0.46, 0 );
      lg.quadraticCurveTo( 0, -size * 0.4, size * 0.46, 0 );
      lg.quadraticCurveTo( 0, size * 0.4, -size * 0.46, 0 );
      lg.fill();
      leafCache.set( key, cv );
      return cv;
    };
    // a plane tree: its trunk in the plane's mottled bark, limbs forking up into the crown; the crown a dome
    // of leaf masses, the far ones deeper in shade, each mass round with the sun on its upper right, its rim
    // broken by single leaves, the limbs showing between the back masses and the front ones
    const tree = ( x: number, z: number, hgt: number, wide: number, r: () => number ) => {
      const s = F / z, [ bx, by ] = P( x, 0, z ), [ cx, cy ] = P( x, hgt * 0.66, z );
      const rx = wide * s, ry = Math.max( 1, ( P( x, hgt, z )[ 1 ] - cy ) * -1 );
      const base: RGB = this.mix( [ 62, 106, 50 ], [ 92, 134, 60 ], r() ), b2 = inAir( base, z );
      const lo = this.mix( b2, [ 16, 36, 26 ], 0.55 ), hi = this.mix( b2, [ 228, 234, 150 ], 0.42 );
      // the trunk: wide at the foot, narrowing, its bark lit at its right
      const tw = Math.max( 1.2, s * 0.75 ), [ , ty ] = P( x, hgt * 0.5, z );
      const trunk = new Path2D();
      trunk.moveTo( bx - tw * 1.5, by ); trunk.quadraticCurveTo( bx - tw, by - tw * 0.6, bx - tw * 0.8, by - tw * 1.6 );
      trunk.lineTo( bx - tw * 0.55, ty ); trunk.lineTo( bx + tw * 0.55, ty ); trunk.lineTo( bx + tw * 0.8, by - tw * 1.6 );
      trunk.quadraticCurveTo( bx + tw, by - tw * 0.6, bx + tw * 1.5, by ); trunk.closePath();
      const tg = g.createLinearGradient( bx - tw, 0, bx + tw, 0 );
      tg.addColorStop( 0, this.rgba( inAir( [ 92, 84, 70 ], z ), 1 ) );
      tg.addColorStop( 0.6, this.rgba( inAir( [ 156, 148, 122 ], z ), 1 ) );
      tg.addColorStop( 1, this.rgba( inAir( [ 204, 196, 168 ], z ), 1 ) );
      g.fillStyle = tg;
      g.fill( trunk );
      // the plane's bark flaking in patches of cream, olive and grey, darker on the shaded side
      g.save();
      g.clip( trunk );
      const bark: RGB[] = [ [ 222, 214, 182 ], [ 168, 162, 120 ], [ 128, 124, 106 ], [ 196, 190, 150 ] ];
      for ( let q = 0; q < 12; q++ ) {
        const px = bx + ( r() * 2 - 1 ) * tw, py = by - ( by - ty ) * r();
        const shade = ( px - bx ) / tw < -0.3 ? 0.35 : 0;
        g.fillStyle = this.rgba( inAir( this.mix( bark[ Math.floor( r() * bark.length ) ], [ 70, 64, 56 ], shade ), z ), 0.75 );
        g.beginPath(); g.ellipse( px, py, tw * ( 0.25 + r() * 0.25 ), tw * ( 0.4 + r() * 0.5 ), ( r() - 0.5 ) * 0.6, 0, Math.PI * 2 ); g.fill();
      }
      g.restore();
      // the limbs: tapering, forking out and up from the trunk's top into the crown
      const limb = ( x0: number, y0: number, x1: number, y1: number, w0: number, w1: number, col: string ) => {
        const mx2 = ( x0 + x1 ) / 2 + ( x0 - x1 ) * 0.15, my2 = ( y0 + y1 ) / 2 + Math.abs( x1 - x0 ) * 0.2;
        const nx0 = y1 - y0, ny0 = -( x1 - x0 ), nl = Math.hypot( nx0, ny0 ) || 1, ux = nx0 / nl, uy = ny0 / nl;
        g.fillStyle = col;
        g.beginPath();
        g.moveTo( x0 + ux * w0, y0 + uy * w0 );
        g.quadraticCurveTo( mx2 + ux * ( w0 + w1 ) / 2, my2 + uy * ( w0 + w1 ) / 2, x1 + ux * w1, y1 + uy * w1 );
        g.lineTo( x1 - ux * w1, y1 - uy * w1 );
        g.quadraticCurveTo( mx2 - ux * ( w0 + w1 ) / 2, my2 - uy * ( w0 + w1 ) / 2, x0 - ux * w0, y0 - uy * w0 );
        g.closePath(); g.fill();
      };
      const limbCol = this.rgba( inAir( [ 132, 122, 102 ], z ), 1 );
      const limbs: [ number, number ][] = [ [ -0.55, 0.15 ], [ -0.2, -0.45 ], [ 0.25, -0.5 ], [ 0.6, 0.05 ] ];
      for ( const [ lx, ly ] of limbs ) { limb( bx + lx * tw * 0.4, ty + tw * 0.5, cx + lx * rx * 0.8, cy + ly * ry * 0.8, tw * 0.42, tw * 0.12, limbCol ); }
      // the crown's masses: one in the middle, a ring round it, a few on top; each with its own depth
      const R0 = Math.min( rx, ry * 1.15 );
      const masses: [ number, number, number, number ][] = [ [ cx, cy, R0 * 0.55, 0.5 ] ];
      const ring = 11;
      for ( let q = 0; q < ring; q++ ) {
        const a = ( q + r() * 0.6 ) / ring * Math.PI * 2, d = 0.5 + r() * 0.18, up = Math.sin( a ) < 0 ? 1.05 : 0.8;
        masses.push( [ cx + Math.cos( a ) * d * rx * 0.92, cy + Math.sin( a ) * d * ry * up, R0 * ( 0.34 + r() * 0.14 ), r() ] );
      }
      for ( let q = 0; q < 4; q++ ) { masses.push( [ cx + ( r() - 0.5 ) * rx * 0.9, cy - ry * ( 0.45 + r() * 0.25 ), R0 * ( 0.3 + r() * 0.12 ), 0.4 + r() * 0.6 ] ); }
      masses.sort( ( a, b ) => a[ 3 ] - b[ 3 ] );
      const SL = [ 0.6, -0.8 ];
      const lumpy = ( qx: number, qy: number, qr: number ) => {
        const n = 16, pts: [ number, number ][] = [];
        for ( let w = 0; w < n; w++ ) { const a = w / n * Math.PI * 2, k = qr * ( 0.86 + r() * 0.2 ); pts.push( [ qx + Math.cos( a ) * k, qy + Math.sin( a ) * k ] ); }
        const p = new Path2D();
        const mid = ( i: number ) => [ ( pts[ i ][ 0 ] + pts[ ( i + 1 ) % n ][ 0 ] ) / 2, ( pts[ i ][ 1 ] + pts[ ( i + 1 ) % n ][ 1 ] ) / 2 ];
        const m0 = mid( n - 1 );
        p.moveTo( m0[ 0 ], m0[ 1 ] );
        for ( let w = 0; w < n; w++ ) { const m1 = mid( w ); p.quadraticCurveTo( pts[ w ][ 0 ], pts[ w ][ 1 ], m1[ 0 ], m1[ 1 ] ); }
        return p;
      };
      const ls = Math.max( 2.2, s * 0.55 );
      let limbsShown = false;
      for ( const [ qx, qy, qr, depth ] of masses ) {
        // the limbs between the back masses and the front ones
        if ( !limbsShown && depth > 0.45 ) {
          limbsShown = true;
          const twig = this.rgba( inAir( [ 104, 94, 78 ], z ), 1 );
          for ( const [ lx, ly ] of limbs ) {
            limb( cx + lx * rx * 0.8, cy + ly * ry * 0.8, cx + lx * rx * 1.0 + ( r() - 0.5 ) * rx * 0.2, cy + ly * ry * 1.0 - ry * 0.25, tw * 0.12, tw * 0.05, twig );
          }
        }
        // its shade: deeper for the masses at the back and those low under the crown
        // and the crown's own light: the masses on its upper right in the sun, so it reads as one dome
        const low = Math.max( 0, Math.min( 1, ( qy - cy ) / ry ) );
        const crownLit = Math.max( -1, Math.min( 1, ( qx - cx ) / rx * 0.7 - ( qy - cy ) / ry * 0.7 ) );
        const dim = Math.max( 0, Math.min( 1, ( 1 - depth ) * 0.22 + low * 0.25 - crownLit * 0.25 + 0.12 ) );
        const mHi = this.mix( hi, b2, dim * 0.7 ), mMid = this.mix( b2, lo, dim * 0.8 ), mLo = this.mix( mMid, lo, 0.55 );
        const body = lumpy( qx, qy, qr );
        const mg = g.createRadialGradient( qx + qr * 0.38, qy - qr * 0.42, qr * 0.05, qx, qy, qr * 1.15 );
        mg.addColorStop( 0, this.rgba( mHi, 1 ) );
        mg.addColorStop( 0.6, this.rgba( mMid, 1 ) );
        mg.addColorStop( 1, this.rgba( mLo, 1 ) );
        g.fillStyle = mg;
        g.fill( body );
        // its leaves: the lit ones crowding toward the sun, the dark ones low at the left, a few outside its
        // rim so the edge reads leafy
        const n = Math.min( 150, Math.floor( qr * qr * 0.22 ) + 8 );
        for ( let q = 0; q < n; q++ ) {
          const a = r() * Math.PI * 2, rim = q < n * 0.3;
          const d = rim ? qr * ( 0.86 + r() * 0.2 ) : Math.sqrt( r() ) * qr * 0.88;
          const lx = qx + Math.cos( a ) * d, ly = qy + Math.sin( a ) * d;
          const lit = ( Math.cos( a ) * SL[ 0 ] + Math.sin( a ) * SL[ 1 ] ) * ( d / qr );
          if ( rim && lit < 0 ) { continue; }
          const tone = lit > 0 ? this.mix( mMid, mHi, Math.min( 1, lit * 1.2 + r() * 0.2 ) ) : this.mix( mMid, mLo, Math.min( 1, -lit * 1.1 + r() * 0.15 ) );
          const sz = ls * ( 0.7 + r() * 0.6 );
          g.drawImage( leaf( tone, Math.floor( r() * 3 ) ), lx - sz / 2, ly - sz / 2, sz, sz );
        }
      }
    };
    items.sort( ( a, b ) => b.d - a.d );
    for ( const it of items ) { it.draw(); }
    this.back = bk.c;
    // the median's layer, between the far lanes' cars and the near ones'
    {
      const mc = canvas();
      g = mc.g;
      if ( drawMedian ) { ( drawMedian as () => void )(); }
      this.median = mc.c;
    }

    // ---- the front layer: the near street lamps and the traffic light pole on the right ----
    const fr = canvas();
    g = fr.g;
    {
      // (kaizen 1) a thing's shadow on the plaza, thrown to the right and away by the afternoon sun
      const castShadow = ( x: number, z: number, hgt: number, wid: number ) => {
        const a = P( x - wid / 2, 0.18, z ), b = P( x + wid / 2, 0.18, z ), c = P( x + wid / 2 - hgt * 1.1, 0.18, z + hgt * 1.0 ), d = P( x - wid / 2 - hgt * 1.1, 0.18, z + hgt * 1.0 );
        poly( [ a, b, c, d ], 'rgba(40,56,96,0.16)' );
      };
      const pole = ( x: number, z: number, hgt: number, arm: number ) => {
        castShadow( x, z, hgt, 0.3 );
        const s = F / z, [ bx, by ] = P( x, 0, z ), [ tx, ty ] = P( x, hgt, z );
        const pg = g.createLinearGradient( bx - s * 0.2, 0, bx + s * 0.2, 0 );
        pg.addColorStop( 0, 'rgb(150,170,120)' );
        pg.addColorStop( 1, 'rgb(70,86,60)' );
        g.fillStyle = pg;
        g.fillRect( bx - s * 0.16, ty, s * 0.32, by - ty );
        g.fillStyle = 'rgb(70,86,60)';
        g.beginPath(); g.moveTo( tx, ty + s * 0.4 ); g.quadraticCurveTo( tx - arm * s * 0.4, ty - s * 0.6, tx - arm * s, ty + s * 0.1 ); g.lineTo( tx - arm * s, ty + s * 0.3 ); g.quadraticCurveTo( tx - arm * s * 0.4, ty - s * 0.3, tx, ty + s * 0.7 ); g.closePath(); g.fill();
        g.fillStyle = 'rgb(40,42,40)';
        g.fillRect( tx - arm * s - s * 0.35, ty + s * 0.3, s * 0.7, s * 1.8 );
        for ( const [ k, c ] of [ [ 0, 'rgb(200,50,40)' ], [ 1, 'rgb(90,90,60)' ], [ 2, 'rgb(60,90,60)' ] ] as [ number, string ][] ) {
          g.fillStyle = c;
          g.beginPath(); g.arc( tx - arm * s, ty + s * ( 0.6 + k * 0.55 ), s * 0.2, 0, Math.PI * 2 ); g.fill();
        }
      };
      pole( 70, 80, 9, 3.2 );
      pole( 150, 92, 8.5, 2.6 );
      // (kaizen 17) Madrid's old cast iron street lamps along the plaza's kerb: a fluted dark green post, its
      // collar and the lantern under a crown
      const lamp = ( x: number, z: number ) => {
        castShadow( x, z, 4.6, 0.3 );
        const s = F / z, [ bx, by ] = P( x, 0, z ), [ tx, ty ] = P( x, 4.6, z );
        const iron = 'rgb(36,52,44)';
        g.fillStyle = iron;
        g.beginPath(); g.moveTo( bx - s * 0.35, by ); g.lineTo( bx + s * 0.35, by ); g.lineTo( bx + s * 0.12, by - s * 1.2 ); g.lineTo( tx + s * 0.07, ty ); g.lineTo( tx - s * 0.07, ty ); g.lineTo( bx - s * 0.12, by - s * 1.2 ); g.closePath(); g.fill();
        g.fillRect( tx - s * 0.2, ty - s * 0.1, s * 0.4, s * 0.16 );
        g.beginPath(); g.moveTo( tx - s * 0.24, ty - s * 0.1 ); g.lineTo( tx + s * 0.24, ty - s * 0.1 ); g.lineTo( tx + s * 0.34, ty - s * 0.95 ); g.lineTo( tx - s * 0.34, ty - s * 0.95 ); g.closePath(); g.fill();
        g.fillStyle = 'rgba(240,236,214,0.92)';
        g.beginPath(); g.moveTo( tx - s * 0.18, ty - s * 0.16 ); g.lineTo( tx + s * 0.18, ty - s * 0.16 ); g.lineTo( tx + s * 0.27, ty - s * 0.88 ); g.lineTo( tx - s * 0.27, ty - s * 0.88 ); g.closePath(); g.fill();
        g.fillStyle = iron;
        g.beginPath(); g.ellipse( tx, ty - s * 0.98, s * 0.42, s * 0.12, 0, 0, Math.PI * 2 ); g.fill();
        g.beginPath(); g.moveTo( tx - s * 0.06, ty - s * 1.05 ); g.lineTo( tx + s * 0.06, ty - s * 1.05 ); g.lineTo( tx, ty - s * 1.4 ); g.closePath(); g.fill();
      };
      const half2 = ( z: number ) => ( W / 2 + 40 ) * z / F;
      // (owner: flowers) round stone planters along the plaza's kerb, heaped with white, pink and red flowers
      const planter = ( x: number, z: number, seed: number ) => {
        castShadow( x, z, 1.4, 2.2 );
        const s = F / z, r4 = rr( seed ), [ px, py ] = P( x, 0, z ), [ , ty ] = P( x, 0.9, z );
        const bowl = g.createLinearGradient( px - s * 1.1, 0, px + s * 1.1, 0 );
        bowl.addColorStop( 0, 'rgb(236,230,220)' );
        bowl.addColorStop( 1, 'rgb(170,166,166)' );
        g.fillStyle = bowl;
        g.beginPath(); g.moveTo( px - s * 1.1, ty ); g.lineTo( px + s * 1.1, ty ); g.lineTo( px + s * 0.8, py ); g.lineTo( px - s * 0.8, py ); g.closePath(); g.fill();
        g.fillStyle = 'rgb(214,208,198)';
        g.beginPath(); g.ellipse( px, ty, s * 1.15, s * 0.22, 0, 0, Math.PI * 2 ); g.fill();
        // (owner) soil inside the rim, dark and moist, a little lighter toward the sun
        const soil = g.createLinearGradient( px - s * 1.0, 0, px + s * 1.0, 0 );
        soil.addColorStop( 0, 'rgb(66,46,32)' );
        soil.addColorStop( 1, 'rgb(104,76,52)' );
        g.fillStyle = soil;
        g.beginPath(); g.ellipse( px, ty + s * 0.01, s * 1.0, s * 0.16, 0, 0, Math.PI * 2 ); g.fill();
        g.fillStyle = 'rgba(40,26,18,0.5)';
        g.beginPath(); g.ellipse( px, ty - s * 0.05, s * 0.96, s * 0.09, 0, Math.PI, Math.PI * 2 ); g.fill();
        // (owner) the leaves and flowers sit 3 px lower, down in the soil
        const dn = 3;
        const cols: RGB[] = [ [ 250, 248, 244 ], [ 250, 248, 244 ], [ 240, 150, 170 ], [ 210, 50, 60 ], [ 70, 120, 56 ] ];
        // a heap of leaves spilling over the rim, the flowers crowded on top, the largest in front
        for ( let q = 0; q < 26; q++ ) {
          const a = r4() * Math.PI * 2, d = Math.sqrt( r4() );
          leafBlade( px + Math.cos( a ) * d * s * 0.9, ty + dn - s * 0.1 - ( 1 - d ) * s * 0.4, s * ( 0.3 + r4() * 0.2 ), Math.atan2( -0.6 - r4(), Math.cos( a ) * 1.2 ), this.mix( [ 60, 110, 50 ], [ 100, 150, 64 ], r4() ) );
        }
        const pts2: [ number, number, number, RGB ][] = [];
        for ( let q = 0; q < 26; q++ ) {
          const a = r4() * Math.PI * 2, d = Math.sqrt( r4() );
          pts2.push( [ px + Math.cos( a ) * d * s * 1.0, ty + dn - s * 0.2 - Math.abs( Math.sin( a ) ) * d * s * 0.15 - ( 1 - d ) * s * 0.5, s * ( 0.16 + r4() * 0.08 ), cols[ Math.floor( r4() * 4 ) ] ] );
        }
        pts2.sort( ( a, b ) => a[ 1 ] - b[ 1 ] );
        for ( const [ fx, fy, fr, fc ] of pts2 ) { flower( fx, fy, fr, fc, fx * 0.7 ); }
      };
      // (owner: the lamps and the flowers never over each other) each lamp stands on screen halfway between
      // two planters, and the lamps, farther off, are drawn first
      const pxs: number[] = [];
      for ( let x = -half2( 72 ) + 19; x < half2( 72 ); x += 26 ) { pxs.push( x ); }
      for ( let q = -1; q < pxs.length; q++ ) {
        const a = q < 0 ? pxs[ 0 ] - 26 : pxs[ q ], b = q + 1 < pxs.length ? pxs[ q + 1 ] : a + 26, x = ( a + b ) / 2 * 77 / 72;
        if ( Math.abs( x - 70 ) > 6 ) { lamp( x, 77 ); }
      }
      for ( const x of pxs ) { planter( x, 72, Math.round( x * 7 ) + 300 ); }
      // (hourly 4) a second row nearer us, larger, between the lamps' row and our feet
      for ( let x = -half2( 46 ) + 8; x < half2( 46 ); x += 19 ) { if ( Math.abs( x - 34 ) > 14 ) { planter( x, 46, Math.round( x * 11 ) + 700 ); } }
      // (hourly 6) an oval bed of flowers on the plaza, its low stone border, red and white in rows
      {
        const cxb = 34, czb = 52, rx = 9, rz = 4.5, r7 = rr( 83 );
        const ring = ( k: number, y: number ) => { const pts: [ number, number ][] = []; for ( let q = 0; q <= 40; q++ ) { const a = q / 40 * Math.PI * 2; pts.push( P( cxb + Math.cos( a ) * rx * k, y, czb + Math.sin( a ) * rz * k ) ); } return pts; };
        poly( ring( 1.04, 0.35 ), 'rgb(206,200,190)' );
        poly( ring( 1.04, 0.0 ).slice( 20 ).concat( ring( 1.04, 0.35 ).slice( 20 ).reverse() ), 'rgb(170,164,156)' );
        poly( ring( 0.96, 0.38 ), 'rgb(78,112,58)' );
        const cols: RGB[] = [ red, [ 214, 54, 64 ], [ 250, 248, 244 ], [ 246, 210, 90 ] ];
        // its leaves first, then the flowers in rings of colour, far ones first so the near ones lie over them
        for ( let q = 0; q < 500; q++ ) {
          const a = r7() * Math.PI * 2, d = Math.sqrt( r7() ) * 0.95, zz = czb + Math.sin( a ) * rz * d;
          const [ lx, ly ] = P( cxb + Math.cos( a ) * rx * d, 0.42, zz ), k7 = F / zz;
          leafBlade( lx, ly, k7 * ( 0.22 + r7() * 0.12 ), -Math.PI / 2 + ( r7() - 0.5 ) * 2.4, this.mix( [ 56, 100, 46 ], [ 92, 140, 60 ], r7() ) );
        }
        const bed: [ number, number, number, RGB ][] = [];
        for ( let q = 0; q < 700; q++ ) {
          const a = r7() * Math.PI * 2, d = Math.sqrt( r7() ) * 0.92, band = Math.floor( d * 4 ), zz = czb + Math.sin( a ) * rz * d;
          const [ fx, fy ] = P( cxb + Math.cos( a ) * rx * d, 0.55 + r7() * 0.15, zz ), k7 = F / zz;
          bed.push( [ fx, fy, k7 * ( 0.12 + r7() * 0.06 ), cols[ band % cols.length ] ] );
        }
        bed.sort( ( a, b ) => a[ 1 ] - b[ 1 ] );
        for ( const [ fx, fy, fr, fc ] of bed ) { flower( fx, fy, fr, fc, fx ); }
      }
    }
    this.front = fr.c;
  }

  /** A point on Gran Vía as a ground (x, 0.01, z) triple for the projection. */
  private gvxy( s: number, v: number ): [ number, number, number ] {
    const [ x, z ] = this.gv( s, v );
    return [ x, 0.03, z ];
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The five colours of the theme's circle: Madrid's cream, gold, amber, red and deep red. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 253, 245, 223 ], [ 232, 194, 93 ], [ 220, 162, 37 ], [ 184, 68, 64 ], [ 144, 41, 38 ] ];
  }
}
